import React, { useEffect, useState } from "react";
import { useAuth } from "@/contexts/AuthContext";
import { Trophy, Building2, Users, Plus, ChevronRight, ArrowLeft, Pencil, Calendar, Video, LayoutGrid, UserCheck, UserX, Shield, Mail, Send, FileText, ToggleLeft, ToggleRight, Link2, Copy, Check, X, MessageSquare, ShieldCheck, Phone, Trash2, UserPlus, DollarSign, TrendingUp, CheckCircle2 } from "lucide-react";
import MatchupManager from "@/components/MatchupManager";
import { fetchAllTeams } from "@/lib/fetchAllTeams";
import { useDynamicPricing, monthlyLabelForTier } from "@/lib/subscriptionPricing";
import { canManageTeams } from "@/lib/adminPermissions";

const AdminPage = () => {
  const { api, user } = useAuth();
  const role = user?.role;
  const canManageTeamHierarchy = canManageTeams(role);
  // pro_league_admin behaves like league_admin everywhere in this page
  // (approvals, teams, schedules); isProLeague gates the PLA-only bits
  // (auto-scoped Subscriptions tab, roster sub badges).
  const isLeagueAdminAny = role === "league_admin" || role === "pro_league_admin";
  const isProLeague = role === "pro_league_admin";
  // League type drives which subscription tier its members buy, so the picker
  // shows each tier's monthly rate — read from Stripe, not hardcoded, so it
  // can't drift from what members are actually charged. Only YSV admins are
  // served both tiers; for anyone else the hint is simply omitted.
  const pricing = useDynamicPricing();
  const youthMonthly = monthlyLabelForTier(pricing, "youth");
  const amateurMonthly = monthlyLabelForTier(pricing, "amateur");
  const youthPriceHint = youthMonthly ? ` (${youthMonthly})` : "";
  const amateurPriceHint = amateurMonthly ? ` (${amateurMonthly})` : "";

  // Determine starting view based on role
  const getInitialView = () => {
    if (role === "ysv_admin") return "leagues";
    if (isLeagueAdminAny) return "orgs";
    if (role === "org_admin") return "teams";
    return "teams";
  };

  const [tab, setTab] = useState("structure");
  const [leagues, setLeagues] = useState([]);
  const [orgs, setOrgs] = useState([]);
  const [teams, setTeams] = useState([]);
  const [allUsers, setAllUsers] = useState([]);
  const [schedules, setSchedules] = useState([]);
  const [pending, setPending] = useState([]);
  const [accessReqs, setAccessReqs] = useState([]);
  const [accessStatus, setAccessStatus] = useState(null); // { kind, text }
  const [view, setView] = useState(getInitialView());
  const [selectedLeague, setSelectedLeague] = useState(null);
  const [selectedOrg, setSelectedOrg] = useState(null);
  const [selectedTeam, setSelectedTeam] = useState(null);
  // Pro League Admin subscription telemetry — cached here so the PLA's own-league
  // stats (fetched once on load) can be reused by both the Orgs-view tile and the
  // Subscriptions tab without re-fetching.
  const [leagueSubStats, setLeagueSubStats] = useState(null); // { total_members, active_subscriptions, by_team:[...] }
  // Roster for whichever team is currently drilled into — lazily fetched (see effect
  // below) only when the viewer can see subscription badges, to avoid N roster
  // fetches for every team up front.
  const [teamRoster, setTeamRoster] = useState(null); // { team_id, team_name, members:[...], active_subscription_count, total_count }

  // Forms
  const [showLeagueForm, setShowLeagueForm] = useState(false);
  const [showOrgForm, setShowOrgForm] = useState(false);
  const [showTeamForm, setShowTeamForm] = useState(false);
  const [showScheduleForm, setShowScheduleForm] = useState(false);
  const [showInviteForm, setShowInviteForm] = useState(false);
  const [editUser, setEditUser] = useState(null);
  const [editLeague, setEditLeague] = useState(null); // {id, name, league_type, av_enabled} when editing an existing league
  const [editOrg, setEditOrg] = useState(null);
  const [editTeam, setEditTeam] = useState(null);
  const [newLeagueName, setNewLeagueName] = useState("");
  const [newLeagueType, setNewLeagueType] = useState("youth"); // "youth" | "amateur"
  const [newOrgName, setNewOrgName] = useState("");
  const [orgStatus, setOrgStatus] = useState(null); // { kind: 'success'|'error', text }
  const [newTeamName, setNewTeamName] = useState("");
  const [inviteForm, setInviteForm] = useState({ email: "", name: "", password: "", role: "player_parent", org_id: "", team_id: "", league_id: "", parent_email: "" });
  const [inviteStatus, setInviteStatus] = useState(null);
  const [schedForm, setSchedForm] = useState({ home_team_id: "", away_team_id: "", game_date: "", game_time: "", location: "", age_group: "" });
  const [activeMatchup, setActiveMatchup] = useState(null);

  useEffect(() => {
    api().get("/leagues").then(r => setLeagues(r.data)).catch(() => {});
    api().get("/organizations").then(r => setOrgs(r.data)).catch(() => {});
    fetchAllTeams(api()).then(setTeams).catch(() => {});
    api().get("/users").then(r => setAllUsers(r.data)).catch(() => {});
    api().get("/schedules").then(r => setSchedules(r.data)).catch(() => {});
    api().get("/approvals/pending").then(r => setPending(r.data)).catch(() => {});
    api().get("/team-access-requests").then(r => setAccessReqs((r.data || []).filter(x => x.status === 'pending'))).catch(() => {});
  }, [api]);

  // Pro League Admin is auto-scoped to their own league — pull subscription stats
  // immediately (no picker, unlike ysv_admin/league_admin on the Subscriptions tab).
  useEffect(() => {
    if (!isProLeague || !user?.league_id) return;
    api().get(`/leagues/${user.league_id}/subscription-stats`).then(r => setLeagueSubStats(r.data)).catch(() => {});
  }, [api, user, isProLeague]);

  // Lazily fetch the roster (for subscription badges) only when a team is drilled
  // into AND the viewer is allowed to see it — avoids N roster fetches on tab load.
  useEffect(() => {
    if (!selectedTeam?.id) { setTeamRoster(null); return; }
    if (!["ysv_admin", "league_admin", "pro_league_admin"].includes(role)) { setTeamRoster(null); return; }
    let cancelled = false;
    api().get(`/teams/${selectedTeam.id}/roster`).then(r => { if (!cancelled) setTeamRoster(r.data); }).catch(() => { if (!cancelled) setTeamRoster(null); });
    return () => { cancelled = true; };
  }, [api, selectedTeam, role]);

  // Actions
  const createLeague = async () => {
    if (!newLeagueName) return;
    try {
      const r = await api().post("/leagues", {
        name: newLeagueName,
        league_type: newLeagueType,
      });
      setLeagues(p => [...p, r.data]);
      setNewLeagueName("");
      setNewLeagueType("youth");
      setShowLeagueForm(false);
    } catch (err) {
      mutationError(err, "Could not create league");
    }
  };
  const saveLeagueEdit = async () => {
    if (!editLeague?.id || !editLeague.name) return;
    try {
      const r = await api().put(`/leagues/${editLeague.id}`, {
        name: editLeague.name,
        description: editLeague.description || "",
        av_enabled: editLeague.av_enabled !== false,
        league_type: editLeague.league_type || "youth",
      });
      setLeagues(p => p.map(l => l.id === editLeague.id ? { ...l, ...r.data } : l));
      setEditLeague(null);
    } catch (err) {
      mutationError(err, "Could not update league");
    }
  };
  const mutationError = (err, fallback) => {
    window.alert(err.response?.data?.detail || fallback);
  };
  const deleteLeague = async () => {
    if (!editLeague?.id || !window.confirm(`Delete unused league "${editLeague.name}"? Leagues with organizations, users, schedules, or season history cannot be deleted.`)) return;
    try {
      await api().delete(`/leagues/${editLeague.id}`);
      setLeagues(p => p.filter(l => l.id !== editLeague.id));
      setEditLeague(null);
      api().get("/organizations").then(r => setOrgs(r.data)).catch(() => {});
      fetchAllTeams(api()).then(setTeams).catch(() => {});
    } catch (err) {
      mutationError(err, "Could not delete league");
    }
  };
  const saveOrgEdit = async () => {
    if (!editOrg?.id || !editOrg.name?.trim()) return;
    try {
      const r = await api().put(`/organizations/${editOrg.id}`, {
        name: editOrg.name.trim(),
        description: editOrg.description || "",
      });
      setOrgs(p => p.map(o => o.id === editOrg.id ? { ...o, ...r.data } : o));
      setSelectedOrg(o => o?.id === editOrg.id ? { ...o, ...r.data } : o);
      setEditOrg(null);
    } catch (err) {
      mutationError(err, "Could not update organization");
    }
  };
  const deleteOrg = async () => {
    if (!editOrg?.id || !window.confirm(`Delete unused organization "${editOrg.name}"? Organizations with teams, users, contacts, or game history cannot be deleted.`)) return;
    try {
      await api().delete(`/organizations/${editOrg.id}`);
      setOrgs(p => p.filter(o => o.id !== editOrg.id));
      if (selectedOrg?.id === editOrg.id) {
        setSelectedOrg(null);
        setView("orgs");
      }
      setEditOrg(null);
    } catch (err) {
      mutationError(err, "Could not delete organization");
    }
  };
  const saveTeamEdit = async () => {
    if (!editTeam?.id || !editTeam.name?.trim()) return;
    try {
      const r = await api().put(`/teams/${editTeam.id}`, {
        name: editTeam.name.trim(),
        org_id: editTeam.org_id,
        description: editTeam.description || "",
        age_group_id: editTeam.age_group_id || null,
      });
      setTeams(p => p.map(t => t.id === editTeam.id ? { ...t, ...r.data } : t));
      setSelectedTeam(t => t?.id === editTeam.id ? { ...t, ...r.data } : t);
      setEditTeam(null);
    } catch (err) {
      mutationError(err, "Could not update team");
    }
  };
  const deleteTeam = async () => {
    if (!editTeam?.id || !window.confirm(`Delete unused team "${editTeam.name}"? Teams with members, schedules, uploads, games, or season history cannot be deleted.`)) return;
    try {
      await api().delete(`/teams/${editTeam.id}`);
      setTeams(p => p.filter(t => t.id !== editTeam.id));
      if (selectedTeam?.id === editTeam.id) {
        setSelectedTeam(null);
        setView("teams");
      }
      setEditTeam(null);
    } catch (err) {
      mutationError(err, "Could not delete team");
    }
  };
  // A club has no league_id column post-remodel, so creating one inside a league
  // has to record the affiliation explicitly — otherwise the new club drops
  // straight into "Unassigned" and vanishes from the league you created it in.
  // A league admin's own league is implied server-side.
  const createOrg = async () => {
    if (!newOrgName) return;
    setOrgStatus(null);
    try {
      const payload = { name: newOrgName };
      if (selectedLeague) payload.league_ids = [selectedLeague.id];
      const r = await api().post("/organizations", payload);
      setOrgs(p => [...p, r.data]);
      setOrgStatus({
        kind: "success",
        text: selectedLeague
          ? `"${r.data.name}" created in ${selectedLeague.name}`
          : `"${r.data.name}" created`,
      });
      setNewOrgName("");
      setShowOrgForm(false);
    } catch (err) {
      setOrgStatus({ kind: "error", text: err.response?.data?.detail || "Could not create organization" });
    }
  };

  // Assign/move an existing club to a league (YSV admin's fix for the clubs that
  // are already sitting in "Unassigned").
  const setOrgLeagues = async (orgId, leagueIds) => {
    setOrgStatus(null);
    try {
      const r = await api().put(`/organizations/${orgId}/leagues`, { league_ids: leagueIds });
      setOrgs(p => p.map(o => (o.id === orgId
        ? { ...o, league_ids: r.data.league_ids, direct_league_ids: r.data.direct_league_ids }
        : o)));
      setOrgStatus({ kind: "success", text: `${r.data.name} updated` });
    } catch (err) {
      setOrgStatus({ kind: "error", text: err.response?.data?.detail || "Could not update leagues" });
    }
  };
  const createTeam = async () => {
    if (!newTeamName) return;
    const oid = selectedOrg?.id || (role === "org_admin" ? user?.org_id : orgs[0]?.id);
    if (!oid) return;
    const r = await api().post("/teams", { name: newTeamName, org_id: oid });
    setTeams(p => [...p, r.data]);
    setNewTeamName(""); setShowTeamForm(false);
  };
  const createSchedule = async () => {
    if (!schedForm.home_team_id || !schedForm.away_team_id || !schedForm.game_date) return;
    const r = await api().post("/schedules", { ...schedForm, league_id: leagues[0]?.id || "" });
    setSchedules(p => [...p, r.data]);
    setSchedForm({ home_team_id: "", away_team_id: "", game_date: "", game_time: "", location: "", age_group: "" });
    setShowScheduleForm(false);
  };
  const processApproval = async (userId, action) => {
    await api().post("/approvals", { user_id: userId, action });
    setPending(p => p.filter(u => u.id !== userId));
  };

  // The Approve/Reject buttons on a team-access request called this and it was
  // never defined, so every click threw a ReferenceError and silently did
  // nothing. Surfacing failures rather than swallowing them, since a dead button
  // is exactly how this went unnoticed.
  const decideAccessRequest = async (requestId, action) => {
    setAccessStatus(null);
    try {
      const r = await api().post(`/team-access-requests/${requestId}/decision`, { action });
      setAccessReqs(p => p.filter(x => x.id !== requestId));
      // The API returns the already-past-tense status ("approved"/"rejected").
      const status = r.data?.status || (action === "approve" ? "approved" : "rejected");
      setAccessStatus({ kind: "success", text: `Request ${status}` });
    } catch (err) {
      setAccessStatus({
        kind: "error",
        text: err.response?.data?.detail || `Could not ${action} this request`,
      });
    }
  };

  const toggleAV = async (leagueId, currentVal) => {
    const r = await api().put(`/av/toggle/${leagueId}`, { av_enabled: !currentVal });
    setLeagues(p => p.map(l => l.id === leagueId ? r.data : l));
  };

  const printAVReport = async (teamId) => {
    try {
      const r = await api().get(`/av/team/${teamId}/pdf`, { responseType: 'blob' });
      const blob = new Blob([r.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `AV_Report_${selectedTeam?.name || 'team'}.pdf`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      setTimeout(() => window.URL.revokeObjectURL(url), 1000);
    } catch (err) {
      console.error('PDF download failed', err);
      alert('Failed to generate PDF report. Please try again.');
    }
  };

  const sendInvite = async () => {
    if (!inviteForm.email || !inviteForm.name || !inviteForm.password) return;
    setInviteStatus(null);

    // ── Direct Add affiliation enforcement (loophole fix) ──
    // YSV/League/Org admins MUST pick the org/team for the invitee, otherwise
    // the new user lands as an orphan invisible to scoped admins.
    const r = inviteForm.role;
    if ((r === "league_admin" || r === "pro_league_admin") && !inviteForm.league_id) {
      setInviteStatus("Please select a league for this admin");
      return;
    }
    if (r === "org_admin" && role !== "org_admin" && !inviteForm.org_id) {
      setInviteStatus("Please select an organization for this admin");
      return;
    }
    if ((r === "coach" || r === "player_parent" || r === "amateur")) {
      // YSV / League admin MUST pick org + team. Org admin only needs team.
      if (role !== "org_admin" && !inviteForm.org_id) {
        setInviteStatus("Please select an organization for this user");
        return;
      }
      if (!inviteForm.team_id) {
        setInviteStatus("Please select a team for this user");
        return;
      }
    }
    if (r === "player_parent" && !inviteForm.parent_email) {
      setInviteStatus("Please provide the parent's email address");
      return;
    }

    try {
      // Determine org_id and team_id
      let org_id = inviteForm.org_id || null;
      let team_id = inviteForm.team_id || null;
      if (role === "org_admin" && !org_id) {
        org_id = user?.org_id;
      }

      const payload = {
        email: inviteForm.email,
        password: inviteForm.password,
        name: inviteForm.name,
        role: inviteForm.role,
        org_id,
        team_id,
      };
      // (Pro) League admins created by YSV Admin need an explicit league_id.
      if (r === "league_admin" || r === "pro_league_admin") {
        payload.league_id = inviteForm.league_id;
      } else if (isLeagueAdminAny && (r === "org_admin" || r === "coach" || r === "player_parent" || r === "amateur")) {
        // League/Pro League admins inviting downstream roles inherit their own league.
        payload.league_id = user?.league_id;
      }
      if (r === "player_parent") payload.parent_email = inviteForm.parent_email;

      await api().post("/auth/register", payload);
      // Auto-approve since admin is inviting
      // Find the new user and approve them
      const usersResp = await api().get("/users");
      const newUser = usersResp.data.find(u => u.email?.toLowerCase() === inviteForm.email.toLowerCase());
      if (newUser && newUser.approval_status === "pending") {
        await api().post("/approvals", { user_id: newUser.id, action: "approve" });
      }
      setInviteStatus("success");
      setInviteForm({ email: "", name: "", password: "", role: "player_parent", org_id: "", team_id: "", league_id: "", parent_email: "" });
      // Refresh users
      api().get("/users").then(r => setAllUsers(r.data)).catch(() => {});
    } catch (err) {
      setInviteStatus(err.response?.data?.detail || "Failed to send invite");
    }
  };

  // Navigation
  const selectLeague = (league) => { setSelectedLeague(league); setView("orgs"); };
  const selectOrg = (org) => { setSelectedOrg(org); setView("teams"); };
  const selectTeam = (team) => { setSelectedTeam(team); setView("players"); };

  const goBack = () => {
    if (view === "players") { setSelectedTeam(null); setView("teams"); }
    else if (view === "teams" && role === "ysv_admin") { setSelectedOrg(null); setView("orgs"); }
    else if (view === "teams" && isLeagueAdminAny) { setSelectedOrg(null); setView("orgs"); }
    else if (view === "orgs" && role === "ysv_admin") { setSelectedLeague(null); setView("leagues"); }
  };

  // Scoped data
  // A club can participate in multiple leagues through different team-season
  // enrollments. Drill-downs use the hydrated plural ids instead of the removed
  // organizations.league_id column.
  const unassignedOrgCount = orgs.filter(o => (o.league_ids || []).length === 0).length;
  const openAllOrgs = () => { setSelectedLeague(null); setSelectedOrg(null); setView("orgs"); };
  const visibleOrgs = selectedLeague
    ? orgs.filter(o => (o.league_ids || []).includes(selectedLeague.id))
    : orgs;
  const visibleTeams = selectedOrg
    ? teams.filter(t => t.org_id === selectedOrg.id && (!selectedLeague || (t.league_ids || []).includes(selectedLeague.id)))
    : teams;
  const teamPlayers = selectedTeam ? allUsers.filter(u => u.team_id === selectedTeam.id) : [];

  // Invite role options per admin role
  const getInviteRoles = () => {
    if (role === "org_admin") return [
      { value: "coach", label: "Coach" },
      { value: "player_parent", label: "Player / Parent" },
      { value: "amateur", label: "Amateur (Adult Player)" },
    ];
    if (isLeagueAdminAny) return [
      { value: "org_admin", label: "Org Admin" },
      { value: "coach", label: "Coach" },
      { value: "amateur", label: "Amateur (Adult Player)" },
    ];
    if (role === "ysv_admin") return [
      { value: "league_admin", label: "League Admin" },
      { value: "pro_league_admin", label: "Pro League Admin" },
      { value: "org_admin", label: "Org Admin" },
      { value: "coach", label: "Coach" },
      { value: "player_parent", label: "Player / Parent" },
      { value: "amateur", label: "Amateur (Adult Player)" },
    ];
    return [];
  };

  const canInvite = ["ysv_admin", "league_admin", "pro_league_admin", "org_admin"].includes(role);
  const canApprove = ["ysv_admin", "league_admin", "pro_league_admin", "org_admin", "coach"].includes(role);
  // Subscriptions tab is exclusive to ysv_admin / league_admin / pro_league_admin.
  const canViewSubs = ["ysv_admin", "league_admin", "pro_league_admin"].includes(role);

  const tabs = [
    { key: "structure", label: "Structure", icon: Trophy, show: true },
    { key: "subscriptions", label: "Subscriptions", icon: DollarSign, show: canViewSubs },
    { key: "invite", label: "Invite", icon: Mail, show: canInvite },
    { key: "schedule", label: "Schedule", icon: Calendar, show: true },
    { key: "approvals", label: `Approvals${(pending.length + accessReqs.length) > 0 ? ` (${pending.length + accessReqs.length})` : ''}`, icon: UserCheck, show: canApprove },
    { key: "gdo", label: "GDO", icon: Video, show: role === "ysv_admin" },
  ].filter(t => t.show);

  // If the current tab is no longer visible to this role, shift to the first visible one.
  useEffect(() => {
    if (!tabs.some(t => t.key === tab) && tabs.length > 0) {
      setTab(tabs[0].key);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tab, tabs.map(t => t.key).join(',')]);

  // Build breadcrumb based on current view and role
  const buildBreadcrumb = () => {
    const crumbs = [];
    if (role === "ysv_admin") {
      if (view === "orgs" || view === "teams" || view === "players") crumbs.push({ label: "Leagues", action: () => { setView("leagues"); setSelectedLeague(null); setSelectedOrg(null); setSelectedTeam(null); } });
      if ((view === "teams" || view === "players") && selectedLeague) crumbs.push({ label: selectedLeague.name, action: () => { setView("orgs"); setSelectedOrg(null); setSelectedTeam(null); } });
      if (view === "players" && selectedOrg) crumbs.push({ label: selectedOrg.name, action: () => { setView("teams"); setSelectedTeam(null); } });
      if (view === "players" && selectedTeam) crumbs.push({ label: selectedTeam.name });
    } else if (isLeagueAdminAny) {
      if ((view === "teams" || view === "players") && selectedOrg) crumbs.push({ label: "Organizations", action: () => { setView("orgs"); setSelectedOrg(null); setSelectedTeam(null); } });
      if (view === "players" && selectedOrg) crumbs.push({ label: selectedOrg.name, action: () => { setView("teams"); setSelectedTeam(null); } });
      if (view === "players" && selectedTeam) crumbs.push({ label: selectedTeam.name });
    } else if (role === "org_admin") {
      if (view === "players" && selectedTeam) crumbs.push({ label: "Teams", action: () => { setView("teams"); setSelectedTeam(null); } });
      if (view === "players" && selectedTeam) crumbs.push({ label: selectedTeam.name });
    }
    return crumbs;
  };

  const breadcrumb = buildBreadcrumb();

  return (
    <div data-testid="admin-page" style={{ padding: '32px 40px' }}>
      {/* Header */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 24 }}>
        <h1 style={{ fontFamily: "'Outfit', sans-serif", fontSize: 28, fontWeight: 700 }}>Admin</h1>
        <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
          {role === "ysv_admin" && <span style={{ background: 'rgba(0,229,255,0.15)', color: 'var(--cyan)', fontSize: 12, fontWeight: 600, padding: '6px 12px', borderRadius: 20 }}>AV ON</span>}
          <span style={{ background: 'rgba(156,39,176,0.15)', color: '#ce93d8', fontSize: 12, fontWeight: 600, padding: '6px 12px', borderRadius: 20 }}>{role?.replace(/_/g, ' ').toUpperCase()}</span>
        </div>
      </div>

      {/* Tabs */}
      <div style={{ display: 'flex', gap: 0, marginBottom: 24, borderBottom: '1px solid var(--dark-border)', overflowX: 'auto' }}>
        {tabs.map(t => (
          <button key={t.key} data-testid={`admin-tab-${t.key}`} onClick={() => setTab(t.key)} style={{
            background: 'none', border: 'none', padding: '10px 16px', color: tab === t.key ? 'var(--cyan)' : 'var(--text-secondary)',
            fontSize: 13, fontWeight: tab === t.key ? 600 : 400, cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 6,
            borderBottom: tab === t.key ? '2px solid var(--cyan)' : '2px solid transparent', marginBottom: -1, fontFamily: "'DM Sans', sans-serif", whiteSpace: 'nowrap',
          }}><t.icon size={14} /> {t.label}</button>
        ))}
      </div>

      {/* ═══ STRUCTURE TAB ═══ */}
      {tab === "structure" && (
        <div>
          {/* Breadcrumb */}
          {breadcrumb.length > 0 && (
            <div style={{ display: 'flex', gap: 8, alignItems: 'center', marginBottom: 16, fontSize: 13 }}>
              {breadcrumb.map((b, i) => (<React.Fragment key={i}>{i > 0 && <span style={{ color: 'var(--text-secondary)' }}>&gt;</span>}{b.action ? <button onClick={b.action} style={{ background: 'none', border: 'none', color: 'var(--cyan)', cursor: 'pointer', fontSize: 13, fontFamily: "'DM Sans', sans-serif" }}>{b.label}</button> : <span style={{ color: 'var(--text-primary)', fontWeight: 600 }}>{b.label}</span>}</React.Fragment>))}
            </div>
          )}

          {/* LEAGUES VIEW — only ysv_admin */}
          {view === "leagues" && role === "ysv_admin" && (
            <div>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
                <h2 style={{ fontSize: 18, fontWeight: 600, display: 'flex', alignItems: 'center', gap: 8 }}><Trophy size={20} style={{ color: 'var(--cyan)' }} /> Leagues</h2>
                <button onClick={() => setShowLeagueForm(true)} data-testid="add-league-btn" style={{ width: 36, height: 36, borderRadius: '50%', background: 'var(--cyan)', border: 'none', color: '#000', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' }}><Plus size={18} /></button>
              </div>
              {showLeagueForm && (
                <div className="glass-card" data-testid="create-league-form" style={{ padding: 20, marginBottom: 12, display: 'flex', flexDirection: 'column', gap: 12 }}>
                  <input className="input-field" data-testid="league-name-input" placeholder="League name" value={newLeagueName} onChange={e => setNewLeagueName(e.target.value)} />
                  <div>
                    <p style={{ fontSize: 11, color: 'var(--text-secondary)', textTransform: 'uppercase', letterSpacing: 0.5, fontWeight: 600, marginBottom: 6 }}>League Type</p>
                    <div style={{ display: 'flex', gap: 8 }}>
                      <button
                        type="button"
                        data-testid="league-type-youth"
                        onClick={() => setNewLeagueType("youth")}
                        style={{
                          flex: 1, padding: '10px 12px', borderRadius: 8, cursor: 'pointer',
                          fontFamily: "'DM Sans', sans-serif", fontWeight: 600, fontSize: 13,
                          background: newLeagueType === "youth" ? 'rgba(0,229,255,0.15)' : 'transparent',
                          border: newLeagueType === "youth" ? '1px solid var(--cyan)' : '1px solid var(--dark-border)',
                          color: newLeagueType === "youth" ? 'var(--cyan)' : 'var(--text-secondary)',
                        }}
                      >Youth{youthPriceHint}</button>
                      <button
                        type="button"
                        data-testid="league-type-amateur"
                        onClick={() => setNewLeagueType("amateur")}
                        style={{
                          flex: 1, padding: '10px 12px', borderRadius: 8, cursor: 'pointer',
                          fontFamily: "'DM Sans', sans-serif", fontWeight: 600, fontSize: 13,
                          background: newLeagueType === "amateur" ? 'rgba(255,107,0,0.15)' : 'transparent',
                          border: newLeagueType === "amateur" ? '1px solid #ff6b00' : '1px solid var(--dark-border)',
                          color: newLeagueType === "amateur" ? '#ff6b00' : 'var(--text-secondary)',
                        }}
                      >Adult / Amateur{amateurPriceHint}</button>
                    </div>
                  </div>
                  <div style={{ display: 'flex', gap: 8 }}>
                    <button onClick={createLeague} data-testid="save-league-btn" className="btn-primary" style={{ flex: 1, padding: '10px 16px', fontSize: 13 }}>Create</button>
                    <button onClick={() => { setShowLeagueForm(false); setNewLeagueName(""); setNewLeagueType("youth"); }} style={{ padding: '10px 16px', background: 'transparent', border: '1px solid var(--dark-border)', borderRadius: 8, color: 'var(--text-secondary)', cursor: 'pointer', fontSize: 13, fontWeight: 600 }}>Cancel</button>
                  </div>
                </div>
              )}
              {leagues.map(league => {
                const isAmateur = league.league_type === "amateur";
                return (
                  <div key={league.id} data-testid={`league-item-${league.id}`} className="glass-card" style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '16px 20px', marginBottom: 8, cursor: 'pointer' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 12, flex: 1 }} onClick={() => selectLeague(league)}>
                      <Trophy size={22} style={{ color: isAmateur ? '#ff6b00' : 'var(--cyan)' }} />
                      <div>
                        <p style={{ fontSize: 15, fontWeight: 600, display: 'flex', alignItems: 'center', gap: 8 }}>
                          {league.name}
                          <span data-testid="league-type-badge" style={{
                            fontSize: 9, padding: '2px 7px', borderRadius: 6, fontWeight: 700, letterSpacing: 0.4,
                            textTransform: 'uppercase',
                            background: isAmateur ? 'rgba(255,107,0,0.15)' : 'rgba(0,229,255,0.12)',
                            color: isAmateur ? '#ff6b00' : 'var(--cyan)',
                            border: isAmateur ? '1px solid rgba(255,107,0,0.35)' : '1px solid rgba(0,229,255,0.30)',
                          }}>{isAmateur ? 'ADULT' : 'YOUTH'}</span>
                        </p>
                        <p style={{ fontSize: 12, color: 'var(--text-secondary)' }}>{orgs.filter(o => (o.league_ids || []).includes(league.id)).length} organizations</p>
                      </div>
                    </div>
                    <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                      <button data-testid={`edit-league-${league.id}`} onClick={(e) => { e.stopPropagation(); setEditLeague({ ...league, league_type: league.league_type || "youth" }); }} title="Edit league" style={{ background: 'rgba(0,229,255,0.1)', border: '1px solid rgba(0,229,255,0.3)', borderRadius: 6, color: 'var(--cyan)', padding: '5px 10px', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 4, fontSize: 11, fontWeight: 600 }}>
                        <Pencil size={11} /> Edit
                      </button>
                      <button data-testid={`av-toggle-${league.id}`} onClick={(e) => { e.stopPropagation(); toggleAV(league.id, league.av_enabled); }} style={{ background: 'none', border: 'none', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 4, padding: '4px 8px', borderRadius: 8, fontSize: 11, fontWeight: 600, color: league.av_enabled ? '#4caf50' : '#888', }}>
                        {league.av_enabled ? <ToggleRight size={22} style={{ color: '#4caf50' }} /> : <ToggleLeft size={22} style={{ color: '#888' }} />}
                        <span>AV</span>
                      </button>
                      <ChevronRight size={18} style={{ color: 'var(--text-secondary)' }} />
                    </div>
                  </div>
                );
              })}

              {/* A club with no league appears under no league, so the drill-down
                  alone can never reach one. This is the way in — and the only
                  place the unassigned ones are actionable. */}
              <div
                data-testid="all-orgs-entry"
                role="button"
                tabIndex={0}
                onClick={openAllOrgs}
                onKeyDown={(e) => {
                  if (e.key === "Enter" || e.key === " ") { e.preventDefault(); openAllOrgs(); }
                }}
                className="glass-card"
                style={{
                  display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                  padding: '16px 20px', marginTop: 16, cursor: 'pointer',
                  border: '1px dashed rgba(255,255,255,0.15)', background: 'transparent',
                }}
              >
                <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                  <Building2 size={22} style={{ color: 'var(--text-secondary)' }} />
                  <div>
                    <p style={{ fontSize: 15, fontWeight: 600 }}>All Organizations</p>
                    <p style={{ fontSize: 12, color: 'var(--text-secondary)' }}>
                      {orgs.length} total
                      {unassignedOrgCount > 0 && (
                        <span style={{ color: '#ff9800' }}> · {unassignedOrgCount} not in any league</span>
                      )}
                    </p>
                  </div>
                </div>
                <ChevronRight size={18} style={{ color: 'var(--text-secondary)' }} />
              </div>
            </div>
          )}

          {/* ORGS VIEW — ysv_admin (drilled in) or league_admin / pro_league_admin (starting point) */}
          {view === "orgs" && (role === "ysv_admin" || isLeagueAdminAny) && (
            <div>
              {role === "ysv_admin" && <button onClick={goBack} style={{ background: 'none', border: 'none', color: 'var(--text-secondary)', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 4, marginBottom: 16, fontSize: 13, fontFamily: "'DM Sans', sans-serif" }}><ArrowLeft size={14} /> Back to Leagues</button>}

              {/* Pro League Admin subscription stat tile — quick visibility of league subscriber health without leaving Structure */}
              {isProLeague && leagueSubStats && (
                <div data-testid="pro-league-stats-tile" className="glass-card" style={{
                  padding: 16, marginBottom: 16,
                  display: 'flex', alignItems: 'center', gap: 16, flexWrap: 'wrap',
                  background: 'linear-gradient(135deg, rgba(76,175,80,0.10), rgba(0,229,255,0.06))',
                  border: '1px solid rgba(76,175,80,0.30)'
                }}>
                  <div style={{ width: 44, height: 44, borderRadius: 10, background: 'rgba(76,175,80,0.15)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                    <TrendingUp size={22} style={{ color: '#4caf50' }} />
                  </div>
                  <div style={{ flex: 1, minWidth: 200 }}>
                    <p style={{ fontSize: 11, color: 'var(--text-secondary)', textTransform: 'uppercase', letterSpacing: 0.5, fontWeight: 600 }}>League Subscriptions</p>
                    <p style={{ fontSize: 22, fontWeight: 700, marginTop: 2 }}>
                      <span style={{ color: '#4caf50' }}>{leagueSubStats.active_subscriptions}</span>
                      <span style={{ color: 'var(--text-secondary)', fontWeight: 400, fontSize: 16 }}> / </span>
                      <span>{leagueSubStats.total_members}</span>
                      <span style={{ color: 'var(--text-secondary)', fontWeight: 400, fontSize: 13, marginLeft: 8 }}>
                        ({leagueSubStats.total_members > 0 ? Math.round(100 * leagueSubStats.active_subscriptions / leagueSubStats.total_members) : 0}% paid)
                      </span>
                    </p>
                  </div>
                  <button onClick={() => setTab("subscriptions")} data-testid="pro-league-view-subs-btn" style={{
                    padding: '8px 14px', background: 'rgba(0,229,255,0.12)', border: '1px solid rgba(0,229,255,0.35)',
                    borderRadius: 8, color: 'var(--cyan)', cursor: 'pointer', fontSize: 12, fontWeight: 600,
                    fontFamily: "'DM Sans', sans-serif", display: 'inline-flex', alignItems: 'center', gap: 6
                  }}><DollarSign size={13} /> View Breakdown</button>
                </div>
              )}

              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
                <h2 style={{ fontSize: 18, fontWeight: 600, display: 'flex', alignItems: 'center', gap: 8 }}><Building2 size={20} style={{ color: '#9c27b0' }} /> Organizations</h2>
                {(role === "ysv_admin" || isLeagueAdminAny) && <button onClick={() => setShowOrgForm(true)} data-testid="add-org-btn" style={{ width: 36, height: 36, borderRadius: '50%', background: 'var(--cyan)', border: 'none', color: '#000', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' }}><Plus size={18} /></button>}
              </div>
              {showOrgForm && (
                <div className="glass-card" style={{ padding: 20, marginBottom: 12 }}>
                  <div style={{ display: 'flex', gap: 12 }}>
                    <input className="input-field" data-testid="org-name-input" placeholder="Organization name" value={newOrgName} onChange={e => setNewOrgName(e.target.value)} style={{ flex: 1 }} />
                    <button onClick={createOrg} data-testid="save-org-btn" className="btn-primary" style={{ padding: '8px 16px', fontSize: 13 }}>Create</button>
                  </div>
                  {selectedLeague && (
                    <p style={{ fontSize: 12, color: 'var(--text-secondary)', marginTop: 8 }}>
                      Will be added to <strong style={{ color: 'var(--cyan)' }}>{selectedLeague.name}</strong>.
                    </p>
                  )}
                </div>
              )}
              {/* Creating a club used to give no feedback at all — the form just
                  closed and the club appeared to vanish. */}
              {orgStatus && (
                <div
                  data-testid="org-status"
                  style={{
                    padding: '10px 14px', borderRadius: 8, marginBottom: 12, fontSize: 13,
                    color: orgStatus.kind === 'success' ? '#4caf50' : '#f44336',
                    background: orgStatus.kind === 'success' ? 'rgba(76,175,80,0.08)' : 'rgba(244,67,54,0.08)',
                    border: `1px solid ${orgStatus.kind === 'success' ? 'rgba(76,175,80,0.3)' : 'rgba(244,67,54,0.3)'}`,
                    display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12,
                  }}
                >
                  <span>{orgStatus.text}</span>
                  <button type="button" aria-label="Dismiss message" onClick={() => setOrgStatus(null)} style={{ background: 'none', border: 'none', color: 'inherit', cursor: 'pointer', padding: 0, lineHeight: 1 }}>
                    <X size={14} />
                  </button>
                </div>
              )}
              {visibleOrgs.map(org => (
                <div key={org.id} data-testid={`org-item-${org.id}`} className="glass-card" style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '16px 20px', marginBottom: 8, cursor: 'pointer' }} onClick={() => selectOrg(org)}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}><Building2 size={22} style={{ color: '#9c27b0' }} /><div><p style={{ fontSize: 15, fontWeight: 600 }}>{org.name}</p><p style={{ fontSize: 12, color: 'var(--text-secondary)' }}>{teams.filter(t => t.org_id === org.id).length} teams</p></div></div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 10 }} onClick={e => e.stopPropagation()}>
                    <button type="button" data-testid={`edit-org-${org.id}`} onClick={e => { e.preventDefault(); e.stopPropagation(); setEditOrg({ ...org }); }} title="Edit organization" style={{ background: 'rgba(0,229,255,0.1)', border: '1px solid rgba(0,229,255,0.3)', borderRadius: 6, color: 'var(--cyan)', padding: '5px 10px', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 4, fontSize: 11, fontWeight: 600 }}>
                      <Pencil size={11} /> Edit
                    </button>
                    {/* YSV admin can move a club between leagues from here — the
                        fix for clubs already stranded in "Unassigned". */}
                    {role === "ysv_admin" && (
                      // Bound to direct_league_ids (what an admin assigned),
                      // NOT league_ids (which also counts leagues a team
                      // enrolled in and so isn't editable from here). Any
                      // enrollment-derived leagues are shown beside it as
                      // read-only context.
                      <>
                        {(org.league_ids || []).length > (org.direct_league_ids || []).length && (
                          <span style={{ fontSize: 11, color: 'var(--text-secondary)' }} title="From team enrollments">
                            +{org.league_ids.length - (org.direct_league_ids || []).length} enrolled
                          </span>
                        )}
                        <select
                          data-testid={`org-league-select-${org.id}`}
                          className="input-field"
                          style={{ fontSize: 12, padding: '4px 8px', width: 190 }}
                          value={(org.direct_league_ids || [])[0] || ""}
                          onChange={e => setOrgLeagues(org.id, e.target.value ? [e.target.value] : [])}
                        >
                          <option value="">— Unassigned —</option>
                          {leagues.map(l => <option key={l.id} value={l.id}>{l.name}</option>)}
                        </select>
                      </>
                    )}
                    <ChevronRight size={18} style={{ color: 'var(--text-secondary)', cursor: 'pointer' }} onClick={() => selectOrg(org)} />
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* TEAMS VIEW — org_admin starts here, or drilled from orgs */}
          {view === "teams" && (
            <div>
              {(role === "ysv_admin" || isLeagueAdminAny) && <button onClick={goBack} style={{ background: 'none', border: 'none', color: 'var(--text-secondary)', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 4, marginBottom: 16, fontSize: 13, fontFamily: "'DM Sans', sans-serif" }}><ArrowLeft size={14} /> Back</button>}
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
                <h2 style={{ fontSize: 18, fontWeight: 600, display: 'flex', alignItems: 'center', gap: 8 }}><Users size={20} style={{ color: '#4caf50' }} /> Teams{selectedOrg ? ` in ${selectedOrg.name}` : ''}</h2>
                <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                  {canManageTeamHierarchy && (selectedOrg || role === "org_admin") && (
                    <button type="button" data-testid="edit-current-org-btn" onClick={() => setEditOrg({ ...(selectedOrg || orgs.find(o => o.id === user?.org_id)) })} disabled={!selectedOrg && !orgs.some(o => o.id === user?.org_id)} title="Edit organization" style={{ height: 36, padding: '0 12px', background: 'rgba(156,39,176,0.12)', border: '1px solid rgba(156,39,176,0.35)', borderRadius: 8, color: '#ce93d8', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 6, fontSize: 12, fontWeight: 600 }}><Pencil size={13} /> Edit Organization</button>
                  )}
                  {canManageTeamHierarchy && (
                    <button onClick={() => setShowTeamForm(true)} data-testid="add-team-btn" style={{ width: 36, height: 36, borderRadius: '50%', background: 'var(--cyan)', border: 'none', color: '#000', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' }}><Plus size={18} /></button>
                  )}
                </div>
              </div>
              {showTeamForm && (
                <div className="glass-card" style={{ padding: 20, marginBottom: 12, display: 'flex', gap: 12 }}>
                  <input className="input-field" data-testid="team-name-input" placeholder="Team name" value={newTeamName} onChange={e => setNewTeamName(e.target.value)} style={{ flex: 1 }} />
                  <button onClick={createTeam} data-testid="save-team-btn" className="btn-primary" style={{ padding: '8px 16px', fontSize: 13 }}>Create</button>
                </div>
              )}
              {visibleTeams.map(team => (
                <div key={team.id} data-testid={`team-item-${team.id}`} className="glass-card" style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '16px 20px', marginBottom: 8, cursor: 'pointer' }} onClick={() => selectTeam(team)}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}><Users size={20} style={{ color: '#4caf50' }} /><div><p style={{ fontSize: 15, fontWeight: 600 }}>{team.name}</p><p style={{ fontSize: 12, color: 'var(--text-secondary)' }}>{team.player_ids?.length || 0} players &bull; {team.coach_ids?.length || 0} coaches</p></div></div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 10 }} onClick={e => e.stopPropagation()}>
                    {canManageTeamHierarchy && (
                      <button type="button" data-testid={`edit-team-${team.id}`} onClick={e => { e.preventDefault(); e.stopPropagation(); setEditTeam({ ...team }); }} title="Edit team" style={{ background: 'rgba(0,229,255,0.1)', border: '1px solid rgba(0,229,255,0.3)', borderRadius: 6, color: 'var(--cyan)', padding: '5px 10px', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 4, fontSize: 11, fontWeight: 600 }}>
                        <Pencil size={11} /> Edit
                      </button>
                    )}
                    <ChevronRight size={18} style={{ color: 'var(--text-secondary)', cursor: 'pointer' }} onClick={() => selectTeam(team)} />
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* PLAYERS VIEW — drill into a team */}
          {view === "players" && selectedTeam && (
            <div>
              <button onClick={goBack} style={{ background: 'none', border: 'none', color: 'var(--text-secondary)', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 4, marginBottom: 16, fontSize: 13, fontFamily: "'DM Sans', sans-serif" }}><ArrowLeft size={14} /> Back to Teams</button>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16, flexWrap: 'wrap', gap: 12 }}>
                <h2 style={{ fontSize: 18, fontWeight: 600, display: 'flex', alignItems: 'center', gap: 8 }}><Users size={20} style={{ color: '#2196f3' }} /> {selectedTeam.name} — Players</h2>
                <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
                  {/* Roster-level subscription summary — visible to ysv_admin / league_admin / pro_league_admin */}
                  {["ysv_admin", "league_admin", "pro_league_admin"].includes(role) && teamRoster && (
                    <span data-testid="team-roster-sub-summary" style={{
                      display: 'inline-flex', alignItems: 'center', gap: 6,
                      padding: '6px 12px', borderRadius: 20,
                      background: 'rgba(76,175,80,0.12)', border: '1px solid rgba(76,175,80,0.35)',
                      color: '#4caf50', fontSize: 12, fontWeight: 600,
                    }}>
                      <DollarSign size={13} />
                      <span data-testid="team-active-sub-count">{teamRoster.active_subscription_count}</span>
                      <span style={{ color: 'var(--text-secondary)', fontWeight: 400 }}> of {teamRoster.total_count} subscribed</span>
                    </span>
                  )}
                  <button data-testid="print-av-report" onClick={() => printAVReport(selectedTeam.id)} style={{ background: 'rgba(0,229,255,0.1)', border: '1px solid rgba(0,229,255,0.3)', borderRadius: 8, color: 'var(--cyan)', padding: '8px 14px', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 6, fontSize: 12, fontWeight: 600, fontFamily: "'DM Sans', sans-serif" }}><FileText size={14} /> Print AV Report</button>
                </div>
              </div>
              {teamPlayers.length === 0 ? (
                <p style={{ color: 'var(--text-secondary)', fontSize: 14, textAlign: 'center', padding: 40 }}>No players on this team yet</p>
              ) : teamPlayers.map(p => {
                // Resolve this player's subscription state from the lazily-fetched roster
                // (only ysv/league/pro-league admins get a roster fetch; see effect above).
                const rosterEntry = teamRoster?.members?.find(m => m.id === p.id);
                const showSubBadge = ["ysv_admin", "league_admin", "pro_league_admin"].includes(role) && !!rosterEntry;
                const subscribed = rosterEntry?.has_active_subscription === true;
                return (
                  <div key={p.id} data-testid={`player-item-${p.id}`} className="glass-card" style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '14px 20px', marginBottom: 8 }}>
                    <div style={{ width: 36, height: 36, borderRadius: '50%', background: p.role === 'coach' ? '#ff6b00' : p.role === 'org_admin' ? '#9c27b0' : '#2196f3', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 14, fontWeight: 700, color: '#fff', flexShrink: 0 }}>{p.name?.charAt(0)}</div>
                    <div style={{ flex: 1 }}>
                      <p style={{ fontSize: 14, fontWeight: 600 }}>{p.name} {p.jersey_number ? <span style={{ color: '#888', fontWeight: 400 }}>#{p.jersey_number}</span> : null}</p>
                      <p style={{ fontSize: 12, color: 'var(--text-secondary)' }}>{p.email}</p>
                    </div>
                    {showSubBadge && (
                      <span data-testid={`sub-badge-${p.id}`} style={{
                        fontSize: 10, padding: '2px 8px', borderRadius: 8, fontWeight: 700, marginRight: 4,
                        display: 'inline-flex', alignItems: 'center', gap: 4,
                        background: subscribed ? 'rgba(76,175,80,0.15)' : 'rgba(255,255,255,0.06)',
                        color: subscribed ? '#4caf50' : '#888',
                        border: subscribed ? '1px solid rgba(76,175,80,0.35)' : '1px solid var(--dark-border)',
                      }}>
                        {subscribed ? <><CheckCircle2 size={11} /> SUB</> : 'NO SUB'}
                      </span>
                    )}
                    <span style={{ fontSize: 10, padding: '2px 8px', borderRadius: 8, fontWeight: 600, marginRight: 4, background: p.av_status === 'verified' ? 'rgba(76,175,80,0.15)' : 'rgba(255,152,0,0.15)', color: p.av_status === 'verified' ? '#4caf50' : '#ff9800' }}>{p.av_status === 'verified' ? 'AV' : 'AV?'}</span>
                    <span style={{ fontSize: 11, background: p.role === 'coach' ? 'rgba(255,107,0,0.15)' : p.role === 'org_admin' ? 'rgba(156,39,176,0.18)' : 'rgba(33,150,243,0.15)', color: p.role === 'coach' ? '#ff6b00' : p.role === 'org_admin' ? '#ce93d8' : '#2196f3', padding: '3px 10px', borderRadius: 10, fontWeight: 600 }}>{p.role?.replace(/_/g, ' ')}</span>
                    <button data-testid={`edit-user-${p.id}`} onClick={() => setEditUser(p)} style={{ background: 'rgba(0,229,255,0.1)', border: '1px solid rgba(0,229,255,0.3)', borderRadius: 6, color: 'var(--cyan)', padding: '5px 10px', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 4, fontSize: 11, fontWeight: 600 }}>
                      <Pencil size={11} /> Edit
                    </button>
                  </div>
                );
              })}
              {editUser && (
                <EditUserModal
                  user={editUser}
                  adminRole={role}
                  api={api}
                  teams={teams}
                  onClose={() => setEditUser(null)}
                  onSaved={(updated) => {
                    setAllUsers(prev => prev.map(p => p.id === updated.id ? { ...p, ...updated } : p));
                    setEditUser(null);
                  }}
                />
              )}
            </div>
          )}
        </div>
      )}

      {/* Edit League modal — global to the Structure tab so it works from any view */}
      {editLeague && (
        <div data-testid="edit-league-modal" style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.7)', zIndex: 50, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16 }}>
          <div className="glass-card" style={{ width: '100%', maxWidth: 460, padding: 24, display: 'flex', flexDirection: 'column', gap: 14 }}>
            <h3 style={{ fontSize: 18, fontWeight: 700, display: 'flex', alignItems: 'center', gap: 8 }}><Pencil size={18} style={{ color: 'var(--cyan)' }} /> Edit League</h3>
            <label style={{ fontSize: 11, color: 'var(--text-secondary)', textTransform: 'uppercase', letterSpacing: 0.5, fontWeight: 600 }}>Name</label>
            <input
              className="input-field"
              data-testid="edit-league-name"
              value={editLeague.name || ""}
              onChange={e => setEditLeague(l => ({ ...l, name: e.target.value }))}
            />
            <label style={{ fontSize: 11, color: 'var(--text-secondary)', textTransform: 'uppercase', letterSpacing: 0.5, fontWeight: 600 }}>League Type</label>
            <div style={{ display: 'flex', gap: 8 }}>
              <button
                type="button"
                data-testid="edit-league-type-youth"
                onClick={() => setEditLeague(l => ({ ...l, league_type: "youth" }))}
                style={{
                  flex: 1, padding: '10px 12px', borderRadius: 8, cursor: 'pointer',
                  fontFamily: "'DM Sans', sans-serif", fontWeight: 600, fontSize: 13,
                  background: editLeague.league_type === "youth" ? 'rgba(0,229,255,0.15)' : 'transparent',
                  border: editLeague.league_type === "youth" ? '1px solid var(--cyan)' : '1px solid var(--dark-border)',
                  color: editLeague.league_type === "youth" ? 'var(--cyan)' : 'var(--text-secondary)',
                }}
              >Youth{youthPriceHint}</button>
              <button
                type="button"
                data-testid="edit-league-type-amateur"
                onClick={() => setEditLeague(l => ({ ...l, league_type: "amateur" }))}
                style={{
                  flex: 1, padding: '10px 12px', borderRadius: 8, cursor: 'pointer',
                  fontFamily: "'DM Sans', sans-serif", fontWeight: 600, fontSize: 13,
                  background: editLeague.league_type === "amateur" ? 'rgba(255,107,0,0.15)' : 'transparent',
                  border: editLeague.league_type === "amateur" ? '1px solid #ff6b00' : '1px solid var(--dark-border)',
                  color: editLeague.league_type === "amateur" ? '#ff6b00' : 'var(--text-secondary)',
                }}
              >Adult / Amateur{amateurPriceHint}</button>
            </div>
            <p style={{ fontSize: 11, color: 'var(--text-secondary)' }}>
              Changing type affects future signups + subscription pricing. Existing
              users keep their current role until you reassign them.
            </p>
            <div style={{ display: 'flex', gap: 8, marginTop: 4 }}>
              <button data-testid="save-edit-league-btn" onClick={saveLeagueEdit} className="btn-primary" style={{ flex: 1, padding: '10px 16px', fontSize: 13 }}>Save Changes</button>
              {role === "ysv_admin" && <button data-testid="delete-league-btn" onClick={deleteLeague} style={{ padding: '10px 16px', background: 'rgba(244,67,54,0.1)', border: '1px solid rgba(244,67,54,0.35)', borderRadius: 8, color: '#f44336', cursor: 'pointer', fontSize: 13, fontWeight: 700 }}>Delete</button>}
              <button data-testid="cancel-edit-league-btn" onClick={() => setEditLeague(null)} style={{ padding: '10px 16px', background: 'transparent', border: '1px solid var(--dark-border)', borderRadius: 8, color: 'var(--text-secondary)', cursor: 'pointer', fontSize: 13, fontWeight: 600 }}>Cancel</button>
            </div>
          </div>
        </div>
      )}

      {editOrg && (
        <div data-testid="edit-org-modal" style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.7)', zIndex: 50, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16 }}>
          <div className="glass-card" style={{ width: '100%', maxWidth: 460, padding: 24, display: 'flex', flexDirection: 'column', gap: 14 }}>
            <h3 style={{ fontSize: 18, fontWeight: 700, display: 'flex', alignItems: 'center', gap: 8 }}><Pencil size={18} style={{ color: 'var(--cyan)' }} /> Edit Organization</h3>
            <label style={{ fontSize: 11, color: 'var(--text-secondary)', textTransform: 'uppercase', letterSpacing: 0.5, fontWeight: 600 }}>Name</label>
            <input className="input-field" data-testid="edit-org-name" value={editOrg.name || ""} onChange={e => setEditOrg(o => ({ ...o, name: e.target.value }))} />
            <label style={{ fontSize: 11, color: 'var(--text-secondary)', textTransform: 'uppercase', letterSpacing: 0.5, fontWeight: 600 }}>Description</label>
            <textarea className="input-field" data-testid="edit-org-description" rows={3} value={editOrg.description || ""} onChange={e => setEditOrg(o => ({ ...o, description: e.target.value }))} style={{ resize: 'vertical' }} />
            <div style={{ display: 'flex', gap: 8, marginTop: 4 }}>
              <button data-testid="save-edit-org-btn" onClick={saveOrgEdit} className="btn-primary" style={{ flex: 1, padding: '10px 16px', fontSize: 13 }}>Save Changes</button>
              {["ysv_admin", "league_admin", "pro_league_admin"].includes(role) && <button data-testid="delete-org-btn" onClick={deleteOrg} style={{ padding: '10px 16px', background: 'rgba(244,67,54,0.1)', border: '1px solid rgba(244,67,54,0.35)', borderRadius: 8, color: '#f44336', cursor: 'pointer', fontSize: 13, fontWeight: 700 }}>Delete</button>}
              <button data-testid="cancel-edit-org-btn" onClick={() => setEditOrg(null)} style={{ padding: '10px 16px', background: 'transparent', border: '1px solid var(--dark-border)', borderRadius: 8, color: 'var(--text-secondary)', cursor: 'pointer', fontSize: 13, fontWeight: 600 }}>Cancel</button>
            </div>
          </div>
        </div>
      )}

      {canManageTeamHierarchy && editTeam && (
        <div data-testid="edit-team-modal" style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.7)', zIndex: 50, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16 }}>
          <div className="glass-card" style={{ width: '100%', maxWidth: 460, padding: 24, display: 'flex', flexDirection: 'column', gap: 14 }}>
            <h3 style={{ fontSize: 18, fontWeight: 700, display: 'flex', alignItems: 'center', gap: 8 }}><Pencil size={18} style={{ color: 'var(--cyan)' }} /> Edit Team</h3>
            <label style={{ fontSize: 11, color: 'var(--text-secondary)', textTransform: 'uppercase', letterSpacing: 0.5, fontWeight: 600 }}>Name</label>
            <input className="input-field" data-testid="edit-team-name" value={editTeam.name || ""} onChange={e => setEditTeam(t => ({ ...t, name: e.target.value }))} />
            <label style={{ fontSize: 11, color: 'var(--text-secondary)', textTransform: 'uppercase', letterSpacing: 0.5, fontWeight: 600 }}>Description</label>
            <textarea className="input-field" data-testid="edit-team-description" rows={3} value={editTeam.description || ""} onChange={e => setEditTeam(t => ({ ...t, description: e.target.value }))} style={{ resize: 'vertical' }} />
            <div style={{ display: 'flex', gap: 8, marginTop: 4 }}>
              <button data-testid="save-edit-team-btn" onClick={saveTeamEdit} className="btn-primary" style={{ flex: 1, padding: '10px 16px', fontSize: 13 }}>Save Changes</button>
              {canManageTeamHierarchy && <button data-testid="delete-team-btn" onClick={deleteTeam} style={{ padding: '10px 16px', background: 'rgba(244,67,54,0.1)', border: '1px solid rgba(244,67,54,0.35)', borderRadius: 8, color: '#f44336', cursor: 'pointer', fontSize: 13, fontWeight: 700 }}>Delete</button>}
              <button data-testid="cancel-edit-team-btn" onClick={() => setEditTeam(null)} style={{ padding: '10px 16px', background: 'transparent', border: '1px solid var(--dark-border)', borderRadius: 8, color: 'var(--text-secondary)', cursor: 'pointer', fontSize: 13, fontWeight: 600 }}>Cancel</button>
            </div>
          </div>
        </div>
      )}

      {/* ═══ SUBSCRIPTIONS TAB (ysv_admin / league_admin / pro_league_admin) ═══ */}
      {tab === "subscriptions" && canViewSubs && (
        <SubscriptionsTab
          api={api}
          role={role}
          user={user}
          leagues={leagues}
          leagueSubStats={leagueSubStats}
          setLeagueSubStats={setLeagueSubStats}
        />
      )}

      {/* ═══ INVITE TAB ═══ */}
      {tab === "invite" && (
        <InviteTabContent
          role={role}
          leagues={leagues}
          orgs={orgs}
          teams={teams}
          getInviteRoles={getInviteRoles}
          user={user}
          api={api}
          inviteForm={inviteForm}
          setInviteForm={setInviteForm}
          sendInvite={sendInvite}
          inviteStatus={inviteStatus}
        />
      )}

      {/* ═══ SCHEDULE TAB ═══ */}
      {tab === "schedule" && (
        <div>
          {activeMatchup ? (
            <MatchupManager scheduleId={activeMatchup} onClose={() => setActiveMatchup(null)} />
          ) : (
            <>
              {["ysv_admin", "league_admin", "pro_league_admin", "org_admin"].includes(role) && (
                <button onClick={() => setShowScheduleForm(!showScheduleForm)} data-testid="create-schedule-btn" style={{ background: 'var(--cyan)', color: '#000', border: 'none', cursor: 'pointer', padding: '12px 20px', borderRadius: 8, fontSize: 14, fontWeight: 600, width: '100%', marginBottom: 24, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8 }}><Calendar size={16} /> Create New Schedule</button>
              )}
              {showScheduleForm && (
                <div data-testid="schedule-form" className="glass-card" style={{ padding: 24, marginBottom: 24 }}>
                  <h3 style={{ fontSize: 16, fontWeight: 600, marginBottom: 16 }}>New Game Schedule</h3>
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
                    <select className="input-field" value={schedForm.home_team_id} onChange={e => setSchedForm({...schedForm, home_team_id: e.target.value})}><option value="">Home Team</option>{teams.map(t => <option key={t.id} value={t.id}>{t.name}</option>)}</select>
                    <select className="input-field" value={schedForm.away_team_id} onChange={e => setSchedForm({...schedForm, away_team_id: e.target.value})}><option value="">Away Team</option>{teams.map(t => <option key={t.id} value={t.id}>{t.name}</option>)}</select>
                    <input className="input-field" type="date" value={schedForm.game_date} onChange={e => setSchedForm({...schedForm, game_date: e.target.value})} />
                    <input className="input-field" placeholder="Time" value={schedForm.game_time} onChange={e => setSchedForm({...schedForm, game_time: e.target.value})} />
                    <input className="input-field" placeholder="Location" value={schedForm.location} onChange={e => setSchedForm({...schedForm, location: e.target.value})} />
                    <input className="input-field" placeholder="Age Group" value={schedForm.age_group} onChange={e => setSchedForm({...schedForm, age_group: e.target.value})} />
                  </div>
                  <button onClick={createSchedule} data-testid="save-schedule-btn" className="btn-primary" style={{ marginTop: 16, width: '100%' }}>Create Schedule</button>
                </div>
              )}
              <h2 style={{ fontSize: 18, fontWeight: 600, marginBottom: 8 }}>Scheduled Games</h2>
              <p style={{ color: 'var(--text-secondary)', fontSize: 13, marginBottom: 16 }}>
                {role === "coach" || role === "player_parent"
                  ? "Your team's upcoming and completed games. Read-only — managed by your org admin."
                  : 'Click "Manage Matchup" to verify players and track play counts. Completed games are read-only.'}
              </p>
              {schedules.length === 0 && (
                <div style={{ textAlign: 'center', padding: 40, background: 'rgba(255,255,255,0.02)', borderRadius: 12, border: '1px dashed var(--dark-border)' }}>
                  <Calendar size={32} style={{ color: 'var(--text-secondary)', opacity: 0.4, marginBottom: 8 }} />
                  <p style={{ fontSize: 14, color: 'var(--text-secondary)' }}>No scheduled games for your hierarchy yet.</p>
                </div>
              )}
              {schedules.map(s => {
                const done = s.status === 'completed';
                const canManage = ["ysv_admin", "league_admin", "pro_league_admin", "org_admin"].includes(role);
                return (
                <div key={s.id} data-testid={`schedule-item-${s.id}`} className="glass-card" style={{ padding: '20px 24px', marginBottom: 12 }}>
                  <span style={{ display: 'inline-block', fontSize: 11, fontWeight: 600, padding: '2px 10px', borderRadius: 12, marginBottom: 8, background: done ? 'rgba(76,175,80,0.15)' : 'rgba(33,150,243,0.15)', color: done ? '#4caf50' : '#2196f3' }}>{s.status?.toUpperCase()}</span>
                  <h3 style={{ fontSize: 16, fontWeight: 600, marginBottom: 4 }}>
                    {done ? (
                      <>
                        {s.home_team_name} <span style={{ color: 'var(--cyan)' }}>({s.home_score ?? 0})</span> <span style={{ color: '#888', margin: '0 6px' }}>vs</span> {s.away_team_name} <span style={{ color: 'var(--cyan)' }}>({s.away_score ?? 0})</span>
                      </>
                    ) : (
                      <>{s.home_team_name} vs {s.away_team_name}</>
                    )}
                  </h3>
                  <div style={{ display: 'flex', gap: 16, color: 'var(--text-secondary)', fontSize: 12, marginBottom: 12, flexWrap: 'wrap' }}><span>{s.game_date}</span><span>{s.game_time}</span><span>{s.location}</span>{s.age_group && <span>{s.age_group}</span>}</div>
                  {canManage ? (
                    <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                      <button data-testid={`manage-matchup-${s.id}`} onClick={() => setActiveMatchup(s.id)} style={{
                        flex: '1 1 auto', padding: '12px', background: done ? '#4caf50' : '#9c27b0', border: 'none',
                        borderRadius: 8, color: '#fff', fontSize: 13, fontWeight: 700, cursor: 'pointer',
                        fontFamily: "'Outfit', sans-serif",
                      }}>{done ? 'View Game Data' : 'Manage Matchup'}</button>
                      {!done && (
                        <button
                          data-testid={`delete-schedule-${s.id}`}
                          onClick={async () => {
                            if (!window.confirm(`Delete schedule "${s.home_team_name} vs ${s.away_team_name}"? This cannot be undone.`)) return;
                            try {
                              await api().delete(`/schedules/${s.id}`);
                              setSchedules(p => p.filter(x => x.id !== s.id));
                            } catch (e) { alert("Delete failed: " + (e.response?.data?.detail || e.message)); }
                          }}
                          style={{
                            padding: '12px 16px', background: 'rgba(244,67,54,0.1)',
                            border: '1px solid rgba(244,67,54,0.35)', borderRadius: 8,
                            color: '#f44336', fontSize: 13, fontWeight: 700, cursor: 'pointer',
                            fontFamily: "'DM Sans', sans-serif",
                          }}
                        >Delete</button>
                      )}
                    </div>
                  ) : (
                    <p style={{ fontSize: 11, color: 'var(--text-secondary)', fontStyle: 'italic' }}>Managed by your organization admin</p>
                  )}
                </div>
                );
              })}
            </>
          )}
        </div>
      )}

      {/* ═══ APPROVALS TAB ═══ */}
      {tab === "approvals" && (
        <div>
          <h2 style={{ fontSize: 18, fontWeight: 600, marginBottom: 8, display: 'flex', alignItems: 'center', gap: 8 }}><Shield size={20} style={{ color: '#ff9800' }} /> Pending Approvals</h2>
          <p style={{ color: 'var(--text-secondary)', fontSize: 13, marginBottom: 24 }}>
            {role === "coach" ? "Approve player/parent sign-ups for your team." :
             role === "org_admin" ? "Approve coaches and player/parents in your organization." :
             isLeagueAdminAny ? "Approve organization admin requests in your league." :
             "Approve league and organization admin requests. Coach & player/parent requests route to their org admin."}
          </p>
          {accessStatus && (
            <div data-testid="access-status" style={{
              padding: '10px 14px', borderRadius: 8, marginBottom: 12, fontSize: 13,
              color: accessStatus.kind === 'success' ? '#4caf50' : '#f44336',
              background: accessStatus.kind === 'success' ? 'rgba(76,175,80,0.08)' : 'rgba(244,67,54,0.08)',
              border: `1px solid ${accessStatus.kind === 'success' ? 'rgba(76,175,80,0.3)' : 'rgba(244,67,54,0.3)'}`,
              display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12,
            }}>
              <span>{accessStatus.text}</span>
              <button type="button" aria-label="Dismiss message" onClick={() => setAccessStatus(null)} style={{ background: 'none', border: 'none', color: 'inherit', cursor: 'pointer', padding: 0, lineHeight: 1 }}>
                <X size={14} />
              </button>
            </div>
          )}
          {pending.length === 0 && accessReqs.length === 0 ? (
            <div style={{ textAlign: 'center', padding: 60 }}>
              <UserCheck size={48} style={{ color: 'var(--text-secondary)', opacity: 0.3, marginBottom: 16 }} />
              <h3 style={{ fontSize: 16, fontWeight: 600, marginBottom: 8 }}>No Pending Approvals</h3>
              <p style={{ color: 'var(--text-secondary)', fontSize: 14 }}>All users are approved.</p>
            </div>
          ) : (
            <>
              {pending.map(u => (
                <div key={u.id} data-testid={`pending-user-${u.id}`} className="glass-card" style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '16px 20px', marginBottom: 8 }}>
                  <div>
                    <p style={{ fontSize: 15, fontWeight: 600 }}>{u.name}</p>
                    <p style={{ fontSize: 12, color: 'var(--cyan)' }}>{u.email}</p>
                    <span style={{ fontSize: 11, background: 'rgba(156,39,176,0.15)', color: '#ce93d8', padding: '2px 8px', borderRadius: 8 }}>{u.role?.replace(/_/g, ' ')}</span>
                  </div>
                  <div style={{ display: 'flex', gap: 8 }}>
                    <button data-testid={`approve-${u.id}`} onClick={() => processApproval(u.id, "approve")} style={{ background: 'rgba(76,175,80,0.15)', border: '1px solid rgba(76,175,80,0.3)', borderRadius: 8, color: '#4caf50', padding: '8px 16px', cursor: 'pointer', fontSize: 13, fontWeight: 600, display: 'flex', alignItems: 'center', gap: 4, fontFamily: "'DM Sans', sans-serif" }}><UserCheck size={14} /> Approve</button>
                    <button data-testid={`reject-${u.id}`} onClick={() => processApproval(u.id, "reject")} style={{ background: 'rgba(244,67,54,0.1)', border: '1px solid rgba(244,67,54,0.2)', borderRadius: 8, color: '#f44336', padding: '8px 16px', cursor: 'pointer', fontSize: 13, fontWeight: 600, display: 'flex', alignItems: 'center', gap: 4, fontFamily: "'DM Sans', sans-serif" }}><UserX size={14} /> Reject</button>
                  </div>
                </div>
              ))}
              {accessReqs.length > 0 && (
                <>
                  <h3 style={{ fontSize: 14, fontWeight: 700, color: 'var(--text-secondary)', textTransform: 'uppercase', letterSpacing: '0.05em', marginTop: 24, marginBottom: 8 }}>Team Access Requests</h3>

                  {accessReqs.map(r => (
                    <div key={r.id} data-testid={`access-request-row-${r.id}`} className="glass-card" style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '16px 20px', marginBottom: 8 }}>
                      <div style={{ flex: 1 }}>
                        <p style={{ fontSize: 15, fontWeight: 600 }}>{r.user_name} <span style={{ color: '#888', fontWeight: 400, fontSize: 12 }}>({r.user_role?.replace(/_/g, ' ')})</span></p>
                        <p style={{ fontSize: 12, color: 'var(--cyan)' }}>{r.user_email}</p>
                        <p style={{ fontSize: 12, color: 'var(--text-secondary)', marginTop: 4 }}>
                          wants access to <strong style={{ color: '#fff' }}>{r.team_name}</strong>
                          {r.target_org_name && <span> · {r.target_org_name}</span>}
                        </p>
                        {r.reason && <p style={{ fontSize: 12, color: '#aaa', fontStyle: 'italic', marginTop: 4 }}>"{r.reason}"</p>}
                      </div>
                      <div style={{ display: 'flex', gap: 8 }}>
                        <button data-testid={`access-approve-${r.id}`} onClick={() => decideAccessRequest(r.id, "approve")} style={{ background: 'rgba(76,175,80,0.15)', border: '1px solid rgba(76,175,80,0.3)', borderRadius: 8, color: '#4caf50', padding: '8px 16px', cursor: 'pointer', fontSize: 13, fontWeight: 600, display: 'flex', alignItems: 'center', gap: 4, fontFamily: "'DM Sans', sans-serif" }}><UserCheck size={14} /> Approve</button>
                        <button data-testid={`access-reject-${r.id}`} onClick={() => decideAccessRequest(r.id, "reject")} style={{ background: 'rgba(244,67,54,0.1)', border: '1px solid rgba(244,67,54,0.2)', borderRadius: 8, color: '#f44336', padding: '8px 16px', cursor: 'pointer', fontSize: 13, fontWeight: 600, display: 'flex', alignItems: 'center', gap: 4, fontFamily: "'DM Sans', sans-serif" }}><UserX size={14} /> Reject</button>
                      </div>
                    </div>
                  ))}
                </>
              )}
            </>
          )}
        </div>
      )}

      {tab === "gdo" && <GDOManagementPanel api={api} schedules={schedules} setSchedules={setSchedules} teams={teams} orgs={orgs} />}
    </div>
  );
};

export default AdminPage;

/* ═══ Invite Tab — tabbed between "Direct Add" and "Invite Link" ═══ */
const InviteTabContent = ({ role, leagues = [], orgs, teams, getInviteRoles, user, api, inviteForm, setInviteForm, sendInvite, inviteStatus }) => {
  const [mode, setMode] = useState("link"); // "link" | "direct"

  // Clubs aren't under a league post-remodel (league comes from team enrollment), so
  // the org picker isn't filtered by the chosen league — show all clubs.
  const visibleOrgs = React.useMemo(() => orgs, [orgs]);

  // Visible teams scoped to the selected org. Post-remodel clubs aren't league-scoped,
  // so there's no league-level team filter — without an org chosen, show all teams.
  const visibleTeams = React.useMemo(() => {
    if (role === "org_admin") return teams; // already scoped by backend
    if (inviteForm.org_id) return teams.filter(t => t.org_id === inviteForm.org_id);
    return teams;
  }, [teams, role, inviteForm.org_id]);

  const r = inviteForm.role;
  const needsLeague = r === "league_admin" || r === "pro_league_admin";
  const needsOrg = (
    (r === "org_admin" && role !== "org_admin") ||
    ((r === "coach" || r === "player_parent" || r === "amateur") && role !== "org_admin")
  );
  const needsTeam = (r === "coach" || r === "player_parent" || r === "amateur");
  const needsParentEmail = r === "player_parent";

  return (
    <div>
      <h2 style={{ fontSize: 18, fontWeight: 600, marginBottom: 8, display: 'flex', alignItems: 'center', gap: 8 }}>
        <Mail size={20} style={{ color: 'var(--cyan)' }} /> Invite Members
      </h2>
      <p style={{ color: 'var(--text-secondary)', fontSize: 13, marginBottom: 20 }}>
        Generate a shareable link. The invitee registers with their own password, then <strong style={{ color: '#ff9800' }}>waits for your approval</strong> before accessing the app.
      </p>

      <div style={{ display: 'flex', gap: 0, marginBottom: 20, borderBottom: '2px solid var(--dark-border)', maxWidth: 500 }}>
        {[
          { key: 'link', label: 'Invite Link', icon: Link2 },
          { key: 'direct', label: 'Direct Add', icon: UserCheck },
        ].map(t => (
          <button key={t.key} data-testid={`invite-mode-${t.key}`} onClick={() => setMode(t.key)} style={{
            background: 'none', border: 'none', padding: '10px 20px',
            color: mode === t.key ? 'var(--cyan)' : 'var(--text-secondary)',
            fontSize: 14, fontWeight: mode === t.key ? 700 : 500,
            borderBottom: mode === t.key ? '2px solid var(--cyan)' : '2px solid transparent',
            cursor: 'pointer', marginBottom: -2,
            fontFamily: "'DM Sans', sans-serif",
            display: 'flex', alignItems: 'center', gap: 6,
          }}>
            <t.icon size={14} /> {t.label}
          </button>
        ))}
      </div>

      {mode === 'link' && <InviteLinkPanel role={role} orgs={orgs} teams={teams} user={user} api={api} />}
      {mode === 'direct' && (
        <div data-testid="invite-form" className="glass-card" style={{ padding: 24, maxWidth: 500 }}>
          <p style={{ fontSize: 12, color: '#888', marginBottom: 14 }}>Create the account yourself and set a temporary password. The user will be <strong style={{ color: '#4caf50' }}>auto-approved</strong>.</p>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
            <input className="input-field" data-testid="invite-name" placeholder="Full Name" value={inviteForm.name} onChange={e => setInviteForm({...inviteForm, name: e.target.value})} />
            <input className="input-field" data-testid="invite-email" type="email" placeholder="Email Address" value={inviteForm.email} onChange={e => setInviteForm({...inviteForm, email: e.target.value})} />
            <input className="input-field" data-testid="invite-password" type="text" placeholder="Temporary Password" value={inviteForm.password} onChange={e => setInviteForm({...inviteForm, password: e.target.value})} />
            <div>
              <label style={{ fontSize: 12, color: 'var(--text-secondary)', marginBottom: 4, display: 'block' }}>Role</label>
              <select className="input-field" data-testid="invite-role" value={inviteForm.role} onChange={e => setInviteForm({ ...inviteForm, role: e.target.value, league_id: "", org_id: "", team_id: "", parent_email: "" })}>
                {getInviteRoles().map(r2 => <option key={r2.value} value={r2.value}>{r2.label}</option>)}
              </select>
            </div>

            {needsLeague && leagues.length > 0 && (
              <div>
                <label style={{ fontSize: 12, color: 'var(--text-secondary)', marginBottom: 4, display: 'block' }}>Assign to League <span style={{ color: '#ff9800' }}>*</span></label>
                <select className="input-field" data-testid="invite-league" value={inviteForm.league_id || ""} onChange={e => setInviteForm({...inviteForm, league_id: e.target.value, org_id: "", team_id: ""})}>
                  <option value="">-- Select League --</option>
                  {leagues.map(l => <option key={l.id} value={l.id}>{l.name}</option>)}
                </select>
              </div>
            )}

            {needsOrg && visibleOrgs.length > 0 && (
              <div>
                <label style={{ fontSize: 12, color: 'var(--text-secondary)', marginBottom: 4, display: 'block' }}>Assign to Organization <span style={{ color: '#ff9800' }}>*</span></label>
                <select className="input-field" data-testid="invite-org" value={inviteForm.org_id || ""} onChange={e => setInviteForm({...inviteForm, org_id: e.target.value, team_id: ""})}>
                  <option value="">-- Select Org --</option>
                  {visibleOrgs.map(o => <option key={o.id} value={o.id}>{o.name}</option>)}
                </select>
              </div>
            )}

            {needsTeam && visibleTeams.length > 0 && (
              <div>
                <label style={{ fontSize: 12, color: 'var(--text-secondary)', marginBottom: 4, display: 'block' }}>Assign to Team <span style={{ color: '#ff9800' }}>*</span></label>
                <select className="input-field" data-testid="invite-team" value={inviteForm.team_id || ""} onChange={e => setInviteForm({...inviteForm, team_id: e.target.value})}>
                  <option value="">-- Select Team --</option>
                  {visibleTeams.map(t => <option key={t.id} value={t.id}>{t.name}</option>)}
                </select>
              </div>
            )}

            {needsParentEmail && (
              <div>
                <label style={{ fontSize: 12, color: 'var(--text-secondary)', marginBottom: 4, display: 'block' }}>Parent's email <span style={{ color: '#ff9800' }}>*</span></label>
                <input className="input-field" data-testid="invite-parent-email" type="email" placeholder="parent@example.com" value={inviteForm.parent_email || ""} onChange={e => setInviteForm({...inviteForm, parent_email: e.target.value})} />
                <p style={{ fontSize: 11, color: '#888', marginTop: 4 }}>We'll email the parent a one-click claim link.</p>
              </div>
            )}

            <button data-testid="send-invite-btn" onClick={sendInvite} className="btn-primary" style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8, marginTop: 8 }}>
              <Send size={16} /> Send Invite
            </button>
            {inviteStatus === "success" && <p data-testid="invite-success" style={{ color: '#4caf50', fontSize: 13, textAlign: 'center' }}>Invite sent and user auto-approved!</p>}
            {inviteStatus && inviteStatus !== "success" && <p data-testid="invite-direct-error" style={{ color: '#f44336', fontSize: 13, textAlign: 'center' }}>{inviteStatus}</p>}
          </div>
        </div>
      )}
    </div>
  );
};

const InviteLinkPanel = ({ role, orgs, teams, user, api }) => {
  const [selOrg, setSelOrg] = useState("");
  const [selTeam, setSelTeam] = useState("");
  const [reusable, setReusable] = useState(true);
  const [generating, setGenerating] = useState(false);
  const [error, setError] = useState(null);
  const [lastInvite, setLastInvite] = useState(null);
  const [copied, setCopied] = useState(false);

  const visibleTeams = role === "org_admin" ? teams : teams.filter(t => !selOrg || t.org_id === selOrg);

  const generate = async () => {
    setError(null); setLastInvite(null); setCopied(false);
    setGenerating(true);
    try {
      const payload = { reusable };  // role & email intentionally omitted — invitee picks role
      if (role === "org_admin") {
        payload.org_id = user?.org_id;
        if (selTeam) payload.team_id = selTeam;
      } else if (role === "league_admin" || role === "pro_league_admin" || role === "ysv_admin") {
        if (selOrg) payload.org_id = selOrg;
        if (selTeam) payload.team_id = selTeam;
      }
      const r = await api().post("/invites", payload);
      const fullUrl = `${window.location.origin}${r.data.invite_path}`;
      setLastInvite({ ...r.data, url: fullUrl });
    } catch (err) {
      setError(err.response?.data?.detail || "Failed to generate invite link");
    } finally {
      setGenerating(false);
    }
  };

  const copyLink = async (url) => {
    try {
      await navigator.clipboard.writeText(url);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      prompt("Copy this invite link:", url);
    }
  };

  const emailLink = (url) => {
    const subject = encodeURIComponent("You're invited to join YSV");
    const body = encodeURIComponent(`${user?.name || "An admin"} invited you to join YSV — Youth Sports Video.\n\nClick this link to register and pick your role:\n${url}\n\nThis invite expires in 14 days.`);
    window.location.href = `mailto:?subject=${subject}&body=${body}`;
  };

  const textLink = (url) => {
    const body = encodeURIComponent(`You're invited to YSV. Sign up here: ${url}`);
    window.location.href = `sms:?&body=${body}`;
  };

  return (
    <div>
      <div className="glass-card" style={{ padding: 24, maxWidth: 580 }}>
        <h3 style={{ fontSize: 14, fontWeight: 700, color: '#888', letterSpacing: '0.06em', textTransform: 'uppercase', marginBottom: 14 }}>Generate Invite Link</h3>

        <div style={{ background: 'rgba(0,229,255,0.06)', border: '1px solid rgba(0,229,255,0.2)', borderRadius: 8, padding: '10px 12px', marginBottom: 16 }}>
          <p style={{ fontSize: 12, color: 'var(--cyan)' }}>The invitee will choose their own role (Coach or Player/Parent) when they sign up.</p>
        </div>

        <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
          {(role === "league_admin" || role === "pro_league_admin" || role === "ysv_admin") && orgs.length > 0 && (
            <div>
              <label style={labelStyle}>Organization (optional)</label>
              <select className="input-field" data-testid="invite-link-org" value={selOrg} onChange={e => { setSelOrg(e.target.value); setSelTeam(""); }}>
                <option value="">— No specific org —</option>
                {orgs.map(o => <option key={o.id} value={o.id}>{o.name}</option>)}
              </select>
            </div>
          )}

          {visibleTeams.length > 0 && (
            <div>
              <label style={labelStyle}>Team (optional)</label>
              <select className="input-field" data-testid="invite-link-team" value={selTeam} onChange={e => setSelTeam(e.target.value)}>
                <option value="">— No specific team —</option>
                {visibleTeams.map(t => <option key={t.id} value={t.id}>{t.name}</option>)}
              </select>
            </div>
          )}

          <div data-testid="invite-reusable-row" style={{
            display: 'flex', alignItems: 'flex-start', gap: 12,
            padding: '12px 14px', background: '#0f141d',
            border: '1px solid #253040', borderRadius: 10,
          }}>
            <input
              type="checkbox"
              data-testid="invite-reusable-toggle"
              checked={reusable}
              onChange={e => setReusable(e.target.checked)}
              style={{ marginTop: 3, accentColor: 'var(--cyan)', width: 16, height: 16 }}
            />
            <div style={{ flex: 1 }}>
              <p style={{ fontSize: 13, fontWeight: 700, color: '#fff', marginBottom: 4 }}>Reusable link</p>
              <p style={{ fontSize: 11, color: 'var(--text-secondary)', lineHeight: 1.5 }}>
                {reusable
                  ? "One link, many sign-ups. Share with your whole team or org. Each new user still requires your approval."
                  : "Single-use link — once accepted, the link can't be used again."}
              </p>
            </div>
          </div>

          <button data-testid="generate-invite-link-btn" onClick={generate} disabled={generating} className="btn-primary" style={{ marginTop: 4, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8, opacity: generating ? 0.7 : 1 }}>
            <Link2 size={16} /> {generating ? "Generating…" : "Generate Invite Link"}
          </button>
          {error && <p data-testid="invite-link-error" style={{ color: '#f44336', fontSize: 13 }}>{error}</p>}
        </div>

        {lastInvite && (
          <div data-testid="invite-link-result" style={{ marginTop: 20, padding: 16, background: 'rgba(0,229,255,0.08)', border: '1px solid rgba(0,229,255,0.3)', borderRadius: 10 }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 10 }}>
              <p style={{ fontSize: 12, fontWeight: 700, color: 'var(--cyan)', letterSpacing: '0.06em', textTransform: 'uppercase' }}>Ready to share</p>
              {lastInvite.reusable && (
                <span data-testid="invite-reusable-badge" style={{
                  padding: '3px 10px', borderRadius: 999, fontSize: 10, fontWeight: 700,
                  letterSpacing: 0.4, textTransform: 'uppercase',
                  color: '#4caf50', background: 'rgba(76,175,80,0.12)', border: '1px solid rgba(76,175,80,0.35)',
                }}>Reusable</span>
              )}
            </div>
            <input data-testid="invite-link-url" readOnly value={lastInvite.url} onFocus={e => e.target.select()} style={{ width: '100%', padding: '10px 12px', background: '#0f141d', border: '1px solid #253040', borderRadius: 8, color: '#fff', fontSize: 12, fontFamily: 'monospace', marginBottom: 12, boxSizing: 'border-box' }} />
            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
              <button data-testid="invite-email-btn" onClick={() => emailLink(lastInvite.url)} style={shareBtnStyle}>
                <Mail size={14} /> Email
              </button>
              <button data-testid="invite-text-btn" onClick={() => textLink(lastInvite.url)} style={shareBtnStyle}>
                <MessageSquare size={14} /> Text
              </button>
              <button data-testid="copy-invite-link-btn" onClick={() => copyLink(lastInvite.url)} style={{ ...shareBtnStyle, background: 'var(--cyan)', color: '#000', borderColor: 'var(--cyan)' }}>
                {copied ? <><Check size={14} /> Copied</> : <><Copy size={14} /> Copy Link</>}
              </button>
            </div>
            <p style={{ fontSize: 11, color: '#888', marginTop: 10 }}>
              Expires in 14 days.{lastInvite.reusable ? " Anyone you share it with can sign up — each new user still requires your approval." : " Single-use — once accepted, the link can't be used again."}
            </p>
          </div>
        )}
      </div>
    </div>
  );
};

const shareBtnStyle = {
  flex: 1, minWidth: 110,
  background: 'transparent', color: '#fff',
  border: '1px solid #253040', borderRadius: 8,
  padding: '10px 14px', fontSize: 13, fontWeight: 700, cursor: 'pointer',
  display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6,
};

const labelStyle = { fontSize: 12, color: 'var(--text-secondary)', marginBottom: 4, display: 'block', fontWeight: 600 };

/* ═══ Edit User Modal — admins edit profile + promote coach <-> org_admin ═══ */
const EditUserModal = ({ user, adminRole, api, teams, onClose, onSaved }) => {
  const [name, setName] = useState(user.name || "");
  const [email, setEmail] = useState(user.email || "");
  const [jersey, setJersey] = useState(user.jersey_number || "");
  const [dob, setDob] = useState(user.player_dob || "");
  const [phone, setPhone] = useState(user.phone || "");
  const [teamId, setTeamId] = useState(user.team_id || "");
  const [currentRole, setCurrentRole] = useState(user.role);
  const [saving, setSaving] = useState(false);
  const [roleSaving, setRoleSaving] = useState(false);
  const [error, setError] = useState(null);

  const isCoachOrAdmin = currentRole === "coach" || currentRole === "org_admin";
  const canTogglePromote = adminRole === "org_admin" && isCoachOrAdmin && user.id !== user.__self_id;

  const visibleTeams = adminRole === "org_admin" ? teams.filter(t => t.org_id === user.org_id) : teams;

  const save = async () => {
    setError(null); setSaving(true);
    try {
      const payload = {
        name: name.trim(),
        email: email.trim() || null,
        jersey_number: jersey || null,
        player_dob: dob || null,
        phone: phone || null,
        team_id: teamId || null,
      };
      const r = await api().put(`/users/${user.id}`, payload);
      onSaved(r.data);
    } catch (err) {
      setError(err.response?.data?.detail || "Failed to save");
    } finally {
      setSaving(false);
    }
  };

  const togglePromote = async () => {
    const next = currentRole === "coach" ? "org_admin" : "coach";
    if (!window.confirm(next === "org_admin"
      ? `Promote ${name} to an Organization Admin? They'll get full admin access for this org.`
      : `Demote ${name} back to Coach? They'll lose admin access.`
    )) return;
    setError(null); setRoleSaving(true);
    try {
      const r = await api().patch(`/users/${user.id}/role`, { role: next });
      setCurrentRole(r.data.role);
      onSaved(r.data);
    } catch (err) {
      setError(err.response?.data?.detail || "Failed to change role");
    } finally {
      setRoleSaving(false);
    }
  };

  const removeFromTeam = async () => {
    if (!window.confirm(`Remove ${name} from their team? They keep their account, subscription, clips, and highlights — they just won't belong to any team until they request to join another one.`)) return;
    setError(null); setRoleSaving(true);
    try {
      await api().post(`/users/${user.id}/remove-from-team`);
      onSaved({ ...user, team_id: null, org_id: null, league_id: null });
      onClose();
    } catch (err) {
      setError(err.response?.data?.detail || "Failed to remove from team");
    } finally {
      setRoleSaving(false);
    }
  };

  return (
    <div onClick={onClose} style={{
      position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.75)', zIndex: 100,
      display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20,
    }}>
      <div data-testid="edit-user-modal" onClick={e => e.stopPropagation()} style={{
        background: '#1a2230', border: '1px solid #253040', borderRadius: 16,
        padding: 28, width: '100%', maxWidth: 500, maxHeight: '90vh', overflowY: 'auto',
      }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 16 }}>
          <h3 style={{ fontSize: 20, fontWeight: 700, fontFamily: "'Outfit', sans-serif" }}>Edit User</h3>
          <button data-testid="edit-user-close" onClick={onClose} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#888' }}>
            <X size={20} />
          </button>
        </div>

        <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 18 }}>
          <span style={{ fontSize: 11, background: currentRole === 'coach' ? 'rgba(255,107,0,0.15)' : currentRole === 'org_admin' ? 'rgba(156,39,176,0.18)' : 'rgba(33,150,243,0.15)', color: currentRole === 'coach' ? '#ff6b00' : currentRole === 'org_admin' ? '#ce93d8' : '#2196f3', padding: '3px 10px', borderRadius: 10, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.05em' }}>
            {currentRole?.replace('_', ' ')}
          </span>
          {canTogglePromote && (
            <button data-testid="toggle-promote-btn" onClick={togglePromote} disabled={roleSaving} style={{
              marginLeft: 'auto',
              background: currentRole === 'coach' ? 'rgba(156,39,176,0.15)' : 'rgba(244,67,54,0.12)',
              color: currentRole === 'coach' ? '#ce93d8' : '#f44336',
              border: `1px solid ${currentRole === 'coach' ? 'rgba(156,39,176,0.35)' : 'rgba(244,67,54,0.3)'}`,
              borderRadius: 8, padding: '8px 14px', fontSize: 12, fontWeight: 700, cursor: 'pointer',
              display: 'flex', alignItems: 'center', gap: 6,
            }}>
              <ShieldCheck size={14} /> {roleSaving ? 'Saving…' : (currentRole === 'coach' ? 'Promote to Org Admin' : 'Demote to Coach')}
            </button>
          )}
        </div>

        <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
          <div><label style={labelStyle}>Name</label><input className="input-field" data-testid="edit-name" value={name} onChange={e => setName(e.target.value)} /></div>
          <div><label style={labelStyle}>Email</label><input className="input-field" data-testid="edit-email" type="email" value={email} onChange={e => setEmail(e.target.value)} /></div>
          {currentRole === 'player_parent' && (
            <>
              <div><label style={labelStyle}>Jersey Number</label><input className="input-field" data-testid="edit-jersey" value={jersey} onChange={e => setJersey(e.target.value)} placeholder="e.g. 7" /></div>
              <div><label style={labelStyle}>Player Date of Birth</label><input className="input-field" data-testid="edit-dob" type="date" value={dob} onChange={e => setDob(e.target.value)} /></div>
            </>
          )}
          <div><label style={labelStyle}>Phone</label><input className="input-field" data-testid="edit-phone" type="tel" value={phone} onChange={e => setPhone(e.target.value)} /></div>
          {visibleTeams.length > 0 && currentRole !== 'org_admin' && (
            <div>
              <label style={labelStyle}>Team</label>
              <select className="input-field" data-testid="edit-team" value={teamId} onChange={e => setTeamId(e.target.value)}>
                <option value="">— No team —</option>
                {visibleTeams.map(t => <option key={t.id} value={t.id}>{t.name}</option>)}
              </select>
            </div>
          )}
        </div>

        {error && <p data-testid="edit-user-error" style={{ color: '#f44336', fontSize: 13, marginTop: 12 }}>{error}</p>}

        {(currentRole === "player_parent" || currentRole === "coach") && user.team_id && (
          <button
            data-testid="remove-from-team-btn"
            onClick={removeFromTeam}
            disabled={roleSaving}
            style={{
              width: '100%', marginTop: 16, padding: 10,
              background: 'rgba(244,67,54,0.1)', border: '1px solid rgba(244,67,54,0.35)',
              borderRadius: 10, color: '#f44336', fontSize: 13, fontWeight: 700,
              cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6,
              opacity: roleSaving ? 0.5 : 1,
              fontFamily: "'DM Sans', sans-serif",
            }}
          >
            <Trash2 size={14} /> Remove from Team
          </button>
        )}

        <div style={{ display: 'flex', gap: 10, marginTop: 20 }}>
          <button onClick={onClose} style={{ flex: 1, padding: 12, background: 'transparent', border: '1px solid #253040', borderRadius: 10, color: '#fff', fontWeight: 600, cursor: 'pointer' }}>Cancel</button>
          <button data-testid="edit-user-save" onClick={save} disabled={saving} className="btn-primary" style={{ flex: 1, opacity: saving ? 0.7 : 1 }}>{saving ? 'Saving…' : 'Save Changes'}</button>
        </div>
      </div>
    </div>
  );
};


/* ═══ GDO Management Panel — YSV admin only ═══ */
const GDOManagementPanel = ({ api, schedules, setSchedules, teams, orgs }) => {
  const [mode, setMode] = useState("operators"); // "operators" | "assignments"
  const [gdos, setGdos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  const [editGdo, setEditGdo] = useState(null);
  const [scheduleGdo, setScheduleGdo] = useState(null); // GDO whose schedule modal is open
  const [form, setForm] = useState({ name: "", phone: "", password: "", email: "", assigned_team_ids: [], assigned_cameras: "" });
  const [error, setError] = useState(null);
  const [saving, setSaving] = useState(false);

  const loadGdos = () => {
    setLoading(true);
    api().get("/gdo/operators").then(r => setGdos(r.data)).catch(() => {}).finally(() => setLoading(false));
  };
  useEffect(() => { loadGdos(); }, []); // eslint-disable-line

  const fmtPhone = (p) => {
    if (!p) return "";
    const d = p.replace(/\D/g, "");
    if (d.length === 10) return `(${d.slice(0,3)}) ${d.slice(3,6)}-${d.slice(6)}`;
    return p;
  };

  const createGdo = async () => {
    setError(null);
    if (!form.name || !form.phone || !form.password) { setError("Name, phone, and password are required"); return; }
    setSaving(true);
    try {
      await api().post("/gdo/operators", {
        name: form.name.trim(), phone: form.phone.trim(),
        password: form.password, email: form.email.trim(),
        assigned_team_ids: form.assigned_team_ids || [],
        assigned_cameras: (form.assigned_cameras || "").trim(),
      });
      setForm({ name: "", phone: "", password: "", email: "", assigned_team_ids: [], assigned_cameras: "" });
      setShowCreate(false);
      loadGdos();
    } catch (e) {
      setError(e.response?.data?.detail || "Failed to create GDO");
    } finally { setSaving(false); }
  };

  const saveEdit = async () => {
    if (!editGdo) return;
    setError(null); setSaving(true);
    try {
      const payload = {
        name: editGdo.name, email: editGdo.email || "", phone: editGdo.phone,
        assigned_team_ids: editGdo.assigned_team_ids || [],
        assigned_cameras: (editGdo.assigned_cameras || "").trim(),
      };
      if (editGdo._newPassword) payload.password = editGdo._newPassword;
      await api().put(`/gdo/operators/${editGdo.id}`, payload);
      setEditGdo(null);
      loadGdos();
    } catch (e) {
      setError(e.response?.data?.detail || "Failed to save");
    } finally { setSaving(false); }
  };

  const deleteGdo = async (g) => {
    if (!window.confirm(`Delete GDO "${g.name}"? This will also clear any game assignments.`)) return;
    try {
      await api().delete(`/gdo/operators/${g.id}`);
      loadGdos();
      // Refresh schedules so assignment badges disappear
      api().get("/schedules").then(r => setSchedules(r.data)).catch(() => {});
    } catch (e) {
      alert(e.response?.data?.detail || "Failed to delete");
    }
  };

  const assignGdo = async (scheduleId, gdoId) => {
    try {
      if (!gdoId) {
        await api().delete(`/schedules/${scheduleId}/assign-gdo`);
      } else {
        await api().post(`/schedules/${scheduleId}/assign-gdo`, { gdo_id: gdoId });
      }
      api().get("/schedules").then(r => setSchedules(r.data)).catch(() => {});
    } catch (e) {
      alert(e.response?.data?.detail || "Failed to update assignment");
    }
  };

  const toggleTeamAssignment = (teamId, list) => {
    const s = new Set(list || []);
    if (s.has(teamId)) s.delete(teamId); else s.add(teamId);
    return Array.from(s);
  };

  return (
    <div data-testid="gdo-management-panel">
      <h2 style={{ fontSize: 18, fontWeight: 600, marginBottom: 8, display: 'flex', alignItems: 'center', gap: 8 }}>
        <Video size={20} style={{ color: 'var(--cyan)' }} /> GDO Management
      </h2>
      <p style={{ color: 'var(--text-secondary)', fontSize: 13, marginBottom: 20 }}>
        Create Gameday Operators and assign them to specific games. GDOs log in at the <strong>/gdo</strong> portal with their phone + password.
      </p>

      <div style={{ display: 'flex', gap: 0, marginBottom: 20, borderBottom: '2px solid var(--dark-border)', maxWidth: 500 }}>
        {[
          { key: 'operators', label: 'Operators', icon: Users },
          { key: 'assignments', label: 'Game Assignments', icon: Calendar },
        ].map(t => (
          <button key={t.key} data-testid={`gdo-mode-${t.key}`} onClick={() => setMode(t.key)} style={{
            background: 'none', border: 'none', padding: '10px 20px',
            color: mode === t.key ? 'var(--cyan)' : 'var(--text-secondary)',
            fontSize: 14, fontWeight: mode === t.key ? 700 : 500,
            borderBottom: mode === t.key ? '2px solid var(--cyan)' : '2px solid transparent',
            cursor: 'pointer', marginBottom: -2,
            fontFamily: "'DM Sans', sans-serif",
            display: 'flex', alignItems: 'center', gap: 6,
          }}>
            <t.icon size={14} /> {t.label}
          </button>
        ))}
      </div>

      {/* ═══ Operators Tab ═══ */}
      {mode === 'operators' && (
        <div>
          <button data-testid="gdo-create-btn" onClick={() => { setShowCreate(true); setError(null); }} className="btn-primary" style={{ display: 'inline-flex', alignItems: 'center', gap: 6, marginBottom: 16 }}>
            <UserPlus size={16} /> New GDO
          </button>

          {showCreate && (
            <div data-testid="gdo-create-form" className="glass-card" style={{ padding: 20, maxWidth: 520, marginBottom: 16 }}>
              <h3 style={{ fontSize: 15, fontWeight: 600, marginBottom: 12 }}>Create Gameday Operator</h3>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                <input className="input-field" data-testid="gdo-name" placeholder="Full Name" value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} />
                <input className="input-field" data-testid="gdo-phone" placeholder="Phone (used to log in)" value={form.phone} onChange={e => setForm({ ...form, phone: e.target.value })} />
                <input className="input-field" data-testid="gdo-password" type="text" placeholder="Password" value={form.password} onChange={e => setForm({ ...form, password: e.target.value })} />
                <input className="input-field" data-testid="gdo-email" placeholder="Email (optional)" value={form.email} onChange={e => setForm({ ...form, email: e.target.value })} />
                <input className="input-field" data-testid="gdo-cameras" placeholder="Assigned camera(s), e.g. XBOT-A1, XBOT-A2" value={form.assigned_cameras} onChange={e => setForm({ ...form, assigned_cameras: e.target.value })} />

                {teams.length > 0 && (
                  <div>
                    <label style={{ fontSize: 12, color: 'var(--text-secondary)', marginBottom: 6, display: 'block' }}>Default Team Access (optional)</label>
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, maxHeight: 120, overflowY: 'auto', padding: 4 }}>
                      {teams.map(t => {
                        const on = (form.assigned_team_ids || []).includes(t.id);
                        return (
                          <button key={t.id} type="button" data-testid={`gdo-team-toggle-${t.id}`} onClick={() => setForm({ ...form, assigned_team_ids: toggleTeamAssignment(t.id, form.assigned_team_ids) })} style={{
                            padding: '4px 10px', borderRadius: 14, fontSize: 12, cursor: 'pointer',
                            background: on ? 'rgba(0,229,255,0.15)' : 'transparent',
                            border: `1px solid ${on ? 'var(--cyan)' : 'var(--dark-border)'}`,
                            color: on ? 'var(--cyan)' : 'var(--text-secondary)', fontFamily: "'DM Sans', sans-serif",
                          }}>{t.name}</button>
                        );
                      })}
                    </div>
                  </div>
                )}

                {error && <p data-testid="gdo-create-error" style={{ color: '#f44336', fontSize: 13 }}>{error}</p>}
                <div style={{ display: 'flex', gap: 10, marginTop: 4 }}>
                  <button onClick={() => { setShowCreate(false); setError(null); }} style={{ flex: 1, padding: 10, background: 'transparent', border: '1px solid var(--dark-border)', borderRadius: 10, color: '#fff', fontWeight: 600, cursor: 'pointer' }}>Cancel</button>
                  <button data-testid="gdo-create-save" onClick={createGdo} disabled={saving} className="btn-primary" style={{ flex: 1, opacity: saving ? 0.6 : 1 }}>{saving ? 'Creating…' : 'Create GDO'}</button>
                </div>
              </div>
            </div>
          )}

          {loading ? (
            <p style={{ color: 'var(--text-secondary)', fontSize: 13 }}>Loading…</p>
          ) : gdos.length === 0 ? (
            <div data-testid="gdo-empty" style={{ padding: 32, textAlign: 'center', color: 'var(--text-secondary)', fontSize: 14 }}>
              No GDOs yet. Click "New GDO" to create one.
            </div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
              {gdos.map(g => (
                <div key={g.id} data-testid={`gdo-row-${g.id}`} className="glass-card" style={{ padding: 14, display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 10 }}>
                  <div style={{ minWidth: 200 }}>
                    <p style={{ fontSize: 15, fontWeight: 600 }}>{g.name}</p>
                    <p style={{ fontSize: 12, color: 'var(--text-secondary)', display: 'flex', alignItems: 'center', gap: 4 }}>
                      <Phone size={12} /> {fmtPhone(g.phone)}
                      {g.email && <><span style={{ opacity: 0.5 }}>•</span><Mail size={12} /> {g.email}</>}
                    </p>
                    <p style={{ fontSize: 11, color: 'var(--text-secondary)', marginTop: 4 }}>
                      {(g.assigned_team_ids || []).length} default team(s) • {schedules.filter(s => s.assigned_gdo_id === g.id).length} game(s) assigned
                    </p>
                  </div>
                  <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                    <button data-testid={`gdo-schedule-${g.id}`} onClick={() => setScheduleGdo(g)} style={{ padding: '6px 12px', background: 'rgba(156,39,176,0.12)', border: '1px solid rgba(156,39,176,0.35)', borderRadius: 8, color: '#ce93d8', cursor: 'pointer', fontSize: 12, fontWeight: 600, display: 'flex', alignItems: 'center', gap: 4, fontFamily: "'DM Sans', sans-serif" }}>
                      <Calendar size={12} /> View Schedule
                    </button>
                    <button data-testid={`gdo-edit-${g.id}`} onClick={() => setEditGdo({ ...g, _newPassword: "" })} style={{ padding: '6px 12px', background: 'rgba(0,229,255,0.1)', border: '1px solid rgba(0,229,255,0.3)', borderRadius: 8, color: 'var(--cyan)', cursor: 'pointer', fontSize: 12, fontWeight: 600, display: 'flex', alignItems: 'center', gap: 4, fontFamily: "'DM Sans', sans-serif" }}>
                      <Pencil size={12} /> Edit
                    </button>
                    <button data-testid={`gdo-delete-${g.id}`} onClick={() => deleteGdo(g)} style={{ padding: '6px 12px', background: 'rgba(244,67,54,0.1)', border: '1px solid rgba(244,67,54,0.3)', borderRadius: 8, color: '#f44336', cursor: 'pointer', fontSize: 12, fontWeight: 600, display: 'flex', alignItems: 'center', gap: 4, fontFamily: "'DM Sans', sans-serif" }}>
                      <Trash2 size={12} /> Delete
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}

          {editGdo && (
            <div data-testid="gdo-edit-modal" onClick={(e) => { if (e.target === e.currentTarget) setEditGdo(null); }} style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.7)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }}>
              <div className="glass-card" style={{ padding: 24, width: 'min(92vw, 480px)', maxHeight: '90vh', overflowY: 'auto' }}>
                <h3 style={{ fontSize: 17, fontWeight: 700, marginBottom: 14 }}>Edit GDO</h3>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                  <label style={{ fontSize: 12, color: 'var(--text-secondary)' }}>Name
                    <input className="input-field" data-testid="gdo-edit-name" value={editGdo.name || ""} onChange={e => setEditGdo({ ...editGdo, name: e.target.value })} />
                  </label>
                  <label style={{ fontSize: 12, color: 'var(--text-secondary)' }}>Phone
                    <input className="input-field" data-testid="gdo-edit-phone" value={editGdo.phone || ""} onChange={e => setEditGdo({ ...editGdo, phone: e.target.value })} />
                  </label>
                  <label style={{ fontSize: 12, color: 'var(--text-secondary)' }}>Email
                    <input className="input-field" data-testid="gdo-edit-email" value={editGdo.email || ""} onChange={e => setEditGdo({ ...editGdo, email: e.target.value })} />
                  </label>
                  <label style={{ fontSize: 12, color: 'var(--text-secondary)' }}>New Password <span style={{ opacity: 0.6 }}>(leave blank to keep current)</span>
                    <input className="input-field" type="text" data-testid="gdo-edit-password" value={editGdo._newPassword || ""} onChange={e => setEditGdo({ ...editGdo, _newPassword: e.target.value })} />
                  </label>
                  <label style={{ fontSize: 12, color: 'var(--text-secondary)' }}>Assigned Camera(s)
                    <input className="input-field" data-testid="gdo-edit-cameras" placeholder="e.g. XBOT-A1, XBOT-A2" value={editGdo.assigned_cameras || ""} onChange={e => setEditGdo({ ...editGdo, assigned_cameras: e.target.value })} />
                  </label>

                  {teams.length > 0 && (
                    <div>
                      <label style={{ fontSize: 12, color: 'var(--text-secondary)', marginBottom: 6, display: 'block' }}>Default Team Access</label>
                      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, maxHeight: 160, overflowY: 'auto', padding: 4 }}>
                        {teams.map(t => {
                          const on = (editGdo.assigned_team_ids || []).includes(t.id);
                          return (
                            <button key={t.id} type="button" data-testid={`gdo-edit-team-toggle-${t.id}`} onClick={() => setEditGdo({ ...editGdo, assigned_team_ids: toggleTeamAssignment(t.id, editGdo.assigned_team_ids) })} style={{
                              padding: '4px 10px', borderRadius: 14, fontSize: 12, cursor: 'pointer',
                              background: on ? 'rgba(0,229,255,0.15)' : 'transparent',
                              border: `1px solid ${on ? 'var(--cyan)' : 'var(--dark-border)'}`,
                              color: on ? 'var(--cyan)' : 'var(--text-secondary)', fontFamily: "'DM Sans', sans-serif",
                            }}>{t.name}</button>
                          );
                        })}
                      </div>
                    </div>
                  )}

                  {error && <p style={{ color: '#f44336', fontSize: 13 }}>{error}</p>}
                  <div style={{ display: 'flex', gap: 10, marginTop: 6 }}>
                    <button onClick={() => setEditGdo(null)} style={{ flex: 1, padding: 10, background: 'transparent', border: '1px solid var(--dark-border)', borderRadius: 10, color: '#fff', fontWeight: 600, cursor: 'pointer' }}>Cancel</button>
                    <button data-testid="gdo-edit-save" onClick={saveEdit} disabled={saving} className="btn-primary" style={{ flex: 1, opacity: saving ? 0.6 : 1 }}>{saving ? 'Saving…' : 'Save'}</button>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      )}

      {/* ═══ Game Assignments Tab ═══ */}
      {mode === 'assignments' && (
        <div>
          <p style={{ fontSize: 13, color: 'var(--text-secondary)', marginBottom: 14 }}>
            Assign a GDO to a scheduled game. They'll get a notification and the game will appear in their GDO portal.
          </p>

          {gdos.length === 0 ? (
            <div className="glass-card" style={{ padding: 20, textAlign: 'center', color: 'var(--text-secondary)', fontSize: 13 }}>
              Create at least one GDO on the Operators tab before assigning games.
            </div>
          ) : schedules.length === 0 ? (
            <div className="glass-card" style={{ padding: 20, textAlign: 'center', color: 'var(--text-secondary)', fontSize: 13 }}>
              No scheduled games. Create a game from the Schedule tab first.
            </div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
              {schedules.map(s => (
                <div key={s.id} data-testid={`gdo-assign-row-${s.id}`} className="glass-card" style={{ padding: 14, display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 10 }}>
                  <div style={{ minWidth: 240, flex: '1 1 260px' }}>
                    <p style={{ fontSize: 14, fontWeight: 600 }}>
                      {s.home_team_name} <span style={{ opacity: 0.5 }}>vs</span> {s.away_team_name}
                    </p>
                    <p style={{ fontSize: 12, color: 'var(--text-secondary)' }}>
                      {s.game_date}{s.game_time ? ` • ${s.game_time}` : ""}{s.location ? ` • ${s.location}` : ""}
                    </p>
                  </div>

                  <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    <select
                      data-testid={`gdo-assign-select-${s.id}`}
                      className="input-field"
                      value={s.assigned_gdo_id || ""}
                      onChange={(e) => assignGdo(s.id, e.target.value)}
                      style={{ minWidth: 200, padding: '8px 10px', fontSize: 13 }}
                    >
                      <option value="">— Unassigned —</option>
                      {gdos.map(g => <option key={g.id} value={g.id}>{g.name} ({fmtPhone(g.phone)})</option>)}
                    </select>
                    {s.assigned_gdo_id && (
                      <span data-testid={`gdo-assign-badge-${s.id}`} style={{ fontSize: 11, padding: '3px 8px', borderRadius: 10, background: 'rgba(76,175,80,0.15)', color: '#4caf50', fontWeight: 600 }}>
                        Assigned
                      </span>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {scheduleGdo && (
        <GDOScheduleModal
          gdo={scheduleGdo}
          onClose={() => setScheduleGdo(null)}
          api={api}
          schedules={schedules}
          setSchedules={setSchedules}
          teams={teams}
        />
      )}
    </div>
  );
};

/* ═══ GDO Schedule Modal — view / assign / edit games for a single GDO ═══ */
const GDOScheduleModal = ({ gdo, onClose, api, schedules, setSchedules, teams }) => {
  const [editRow, setEditRow] = useState(null);
  const [editDraft, setEditDraft] = useState({});
  const [addPickerOpen, setAddPickerOpen] = useState(false);
  const [pickScheduleId, setPickScheduleId] = useState("");
  const [showNewGame, setShowNewGame] = useState(false);
  const [newGame, setNewGame] = useState({ home_team_id: "", away_team_id: "", game_date: "", game_time: "", location: "", age_group: "" });
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);

  const assignedToThisGdo = schedules.filter(s => s.assigned_gdo_id === gdo.id);
  const unassigned = schedules.filter(s => !s.assigned_gdo_id);

  const refreshSchedules = async () => {
    try { const r = await api().get("/schedules"); setSchedules(r.data); } catch {}
  };

  const startEdit = (s) => {
    setEditRow(s.id);
    setEditDraft({
      game_date: s.game_date || "",
      game_time: s.game_time || "",
      location: s.location || "",
      home_team_id: s.home_team_id || "",
      away_team_id: s.away_team_id || "",
      age_group: s.age_group || "",
    });
    setError(null);
  };

  const saveEdit = async (s) => {
    setBusy(true); setError(null);
    try {
      await api().put(`/schedules/${s.id}`, editDraft);
      await refreshSchedules();
      setEditRow(null);
    } catch (e) {
      setError(e.response?.data?.detail || "Failed to save");
    } finally { setBusy(false); }
  };

  const assignExisting = async () => {
    if (!pickScheduleId) return;
    setBusy(true); setError(null);
    try {
      await api().post(`/schedules/${pickScheduleId}/assign-gdo`, { gdo_id: gdo.id });
      await refreshSchedules();
      setPickScheduleId("");
      setAddPickerOpen(false);
    } catch (e) {
      setError(e.response?.data?.detail || "Failed to assign");
    } finally { setBusy(false); }
  };

  const unassignGame = async (s) => {
    if (!window.confirm(`Unassign this game from ${gdo.name}?`)) return;
    setBusy(true);
    try {
      await api().delete(`/schedules/${s.id}/assign-gdo`);
      await refreshSchedules();
    } catch (e) {
      alert(e.response?.data?.detail || "Failed to unassign");
    } finally { setBusy(false); }
  };

  const createAndAssign = async () => {
    if (!newGame.home_team_id || !newGame.away_team_id || !newGame.game_date) {
      setError("Home team, away team, and game date are required");
      return;
    }
    if (newGame.home_team_id === newGame.away_team_id) {
      setError("Home and away team cannot be the same");
      return;
    }
    setBusy(true); setError(null);
    try {
      const r = await api().post("/schedules", { ...newGame, league_id: "" });
      await api().post(`/schedules/${r.data.id}/assign-gdo`, { gdo_id: gdo.id });
      await refreshSchedules();
      setNewGame({ home_team_id: "", away_team_id: "", game_date: "", game_time: "", location: "", age_group: "" });
      setShowNewGame(false);
    } catch (e) {
      setError(e.response?.data?.detail || "Failed to create game");
    } finally { setBusy(false); }
  };

  return (
    <div data-testid="gdo-schedule-modal" onClick={(e) => { if (e.target === e.currentTarget) onClose(); }} style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.75)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1001, padding: 16 }}>
      <div className="glass-card" style={{ padding: 24, width: 'min(96vw, 780px)', maxHeight: '92vh', overflowY: 'auto' }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 4 }}>
          <h3 style={{ fontSize: 18, fontWeight: 700, display: 'flex', alignItems: 'center', gap: 8 }}>
            <Calendar size={18} style={{ color: 'var(--cyan)' }} /> {gdo.name}'s Schedule
          </h3>
          <button onClick={onClose} data-testid="gdo-schedule-close" style={{ background: 'none', border: 'none', color: '#888', cursor: 'pointer', padding: 4 }}>
            <X size={20} />
          </button>
        </div>
        <p style={{ fontSize: 12, color: 'var(--text-secondary)', marginBottom: 18 }}>
          Games assigned to this GDO. Edits here update the shared Schedule tab in real time.
        </p>

        <h4 style={{ fontSize: 13, fontWeight: 700, color: 'var(--text-secondary)', textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: 8 }}>
          Assigned Games ({assignedToThisGdo.length})
        </h4>
        {assignedToThisGdo.length === 0 ? (
          <div data-testid="gdo-schedule-empty" style={{ padding: 16, border: '1px dashed var(--dark-border)', borderRadius: 10, textAlign: 'center', color: 'var(--text-secondary)', fontSize: 13, marginBottom: 14 }}>
            No games assigned yet. Use the buttons below to assign or create one.
          </div>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8, marginBottom: 14 }}>
            {assignedToThisGdo.map(s => (
              <div key={s.id} data-testid={`gdo-sched-row-${s.id}`} style={{ background: 'rgba(255,255,255,0.02)', border: '1px solid var(--dark-border)', borderRadius: 10, padding: 12 }}>
                {editRow === s.id ? (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
                      <div>
                        <label style={{ fontSize: 11, color: 'var(--text-secondary)' }}>Home Team</label>
                        <select className="input-field" data-testid={`gdo-sched-edit-home-${s.id}`} value={editDraft.home_team_id} onChange={e => setEditDraft({ ...editDraft, home_team_id: e.target.value })}>
                          {teams.map(t => <option key={t.id} value={t.id}>{t.name}</option>)}
                        </select>
                      </div>
                      <div>
                        <label style={{ fontSize: 11, color: 'var(--text-secondary)' }}>Away Team</label>
                        <select className="input-field" data-testid={`gdo-sched-edit-away-${s.id}`} value={editDraft.away_team_id} onChange={e => setEditDraft({ ...editDraft, away_team_id: e.target.value })}>
                          {teams.map(t => <option key={t.id} value={t.id}>{t.name}</option>)}
                        </select>
                      </div>
                    </div>
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
                      <div>
                        <label style={{ fontSize: 11, color: 'var(--text-secondary)' }}>Date</label>
                        <input className="input-field" type="date" data-testid={`gdo-sched-edit-date-${s.id}`} value={editDraft.game_date} onChange={e => setEditDraft({ ...editDraft, game_date: e.target.value })} />
                      </div>
                      <div>
                        <label style={{ fontSize: 11, color: 'var(--text-secondary)' }}>Time</label>
                        <input className="input-field" data-testid={`gdo-sched-edit-time-${s.id}`} placeholder="e.g. 3:00 PM" value={editDraft.game_time} onChange={e => setEditDraft({ ...editDraft, game_time: e.target.value })} />
                      </div>
                    </div>
                    <div>
                      <label style={{ fontSize: 11, color: 'var(--text-secondary)' }}>Location</label>
                      <input className="input-field" data-testid={`gdo-sched-edit-location-${s.id}`} value={editDraft.location} onChange={e => setEditDraft({ ...editDraft, location: e.target.value })} />
                    </div>
                    <div style={{ display: 'flex', gap: 8, marginTop: 4 }}>
                      <button onClick={() => setEditRow(null)} style={{ flex: 1, padding: 8, background: 'transparent', border: '1px solid var(--dark-border)', borderRadius: 8, color: '#fff', fontWeight: 600, cursor: 'pointer' }}>Cancel</button>
                      <button data-testid={`gdo-sched-edit-save-${s.id}`} onClick={() => saveEdit(s)} disabled={busy} className="btn-primary" style={{ flex: 1, opacity: busy ? 0.6 : 1 }}>{busy ? 'Saving…' : 'Save'}</button>
                    </div>
                  </div>
                ) : (
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 10 }}>
                    <div style={{ minWidth: 200, flex: '1 1 260px' }}>
                      <p style={{ fontSize: 14, fontWeight: 600 }}>
                        {s.home_team_name} <span style={{ opacity: 0.5 }}>vs</span> {s.away_team_name}
                      </p>
                      <p style={{ fontSize: 12, color: 'var(--text-secondary)', marginTop: 2 }}>
                        {s.game_date}{s.game_time ? ` • ${s.game_time}` : ""}
                      </p>
                      <p style={{ fontSize: 12, color: s.location ? 'var(--cyan)' : '#ff9800', marginTop: 2 }}>
                        {s.location || "⚠ No location set"}
                      </p>
                    </div>
                    <div style={{ display: 'flex', gap: 6 }}>
                      <button data-testid={`gdo-sched-edit-${s.id}`} onClick={() => startEdit(s)} style={{ padding: '6px 12px', background: 'rgba(0,229,255,0.1)', border: '1px solid rgba(0,229,255,0.3)', borderRadius: 8, color: 'var(--cyan)', cursor: 'pointer', fontSize: 12, fontWeight: 600, display: 'flex', alignItems: 'center', gap: 4, fontFamily: "'DM Sans', sans-serif" }}>
                        <Pencil size={12} /> Edit
                      </button>
                      <button data-testid={`gdo-sched-unassign-${s.id}`} onClick={() => unassignGame(s)} style={{ padding: '6px 12px', background: 'rgba(244,67,54,0.1)', border: '1px solid rgba(244,67,54,0.3)', borderRadius: 8, color: '#f44336', cursor: 'pointer', fontSize: 12, fontWeight: 600, display: 'flex', alignItems: 'center', gap: 4, fontFamily: "'DM Sans', sans-serif" }}>
                        <X size={12} /> Unassign
                      </button>
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>
        )}

        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: 12 }}>
          <button data-testid="gdo-sched-assign-existing-toggle" onClick={() => { setAddPickerOpen(!addPickerOpen); setShowNewGame(false); }} style={{ padding: '8px 14px', background: addPickerOpen ? 'rgba(0,229,255,0.2)' : 'rgba(0,229,255,0.08)', border: '1px solid rgba(0,229,255,0.3)', borderRadius: 10, color: 'var(--cyan)', cursor: 'pointer', fontSize: 13, fontWeight: 600, display: 'flex', alignItems: 'center', gap: 6, fontFamily: "'DM Sans', sans-serif" }}>
            <Link2 size={14} /> Assign Existing Game
          </button>
          <button data-testid="gdo-sched-new-toggle" onClick={() => { setShowNewGame(!showNewGame); setAddPickerOpen(false); }} style={{ padding: '8px 14px', background: showNewGame ? 'rgba(76,175,80,0.2)' : 'rgba(76,175,80,0.08)', border: '1px solid rgba(76,175,80,0.3)', borderRadius: 10, color: '#4caf50', cursor: 'pointer', fontSize: 13, fontWeight: 600, display: 'flex', alignItems: 'center', gap: 6, fontFamily: "'DM Sans', sans-serif" }}>
            <Plus size={14} /> Add New Game
          </button>
        </div>

        {addPickerOpen && (
          <div data-testid="gdo-sched-picker" style={{ background: 'rgba(0,229,255,0.05)', border: '1px solid rgba(0,229,255,0.2)', borderRadius: 10, padding: 12, marginBottom: 12 }}>
            <p style={{ fontSize: 12, color: 'var(--text-secondary)', marginBottom: 8 }}>Pick an unassigned game from the Schedule:</p>
            {unassigned.length === 0 ? (
              <p style={{ fontSize: 13, color: '#ff9800' }}>No unassigned games. Create a new one below.</p>
            ) : (
              <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
                <select data-testid="gdo-sched-picker-select" className="input-field" style={{ flex: 1, minWidth: 240 }} value={pickScheduleId} onChange={e => setPickScheduleId(e.target.value)}>
                  <option value="">— Select a game —</option>
                  {unassigned.map(s => <option key={s.id} value={s.id}>{s.game_date} — {s.home_team_name} vs {s.away_team_name}{s.location ? ` @ ${s.location}` : ""}</option>)}
                </select>
                <button data-testid="gdo-sched-picker-save" onClick={assignExisting} disabled={!pickScheduleId || busy} className="btn-primary" style={{ opacity: (!pickScheduleId || busy) ? 0.5 : 1 }}>Assign</button>
              </div>
            )}
          </div>
        )}

        {showNewGame && (
          <div data-testid="gdo-sched-new-form" style={{ background: 'rgba(76,175,80,0.05)', border: '1px solid rgba(76,175,80,0.2)', borderRadius: 10, padding: 12, marginBottom: 12 }}>
            <p style={{ fontSize: 12, color: 'var(--text-secondary)', marginBottom: 8 }}>Create a new game and auto-assign {gdo.name} to film it:</p>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
                <select data-testid="gdo-sched-new-home" className="input-field" value={newGame.home_team_id} onChange={e => setNewGame({ ...newGame, home_team_id: e.target.value })}>
                  <option value="">Home team</option>
                  {teams.map(t => <option key={t.id} value={t.id}>{t.name}</option>)}
                </select>
                <select data-testid="gdo-sched-new-away" className="input-field" value={newGame.away_team_id} onChange={e => setNewGame({ ...newGame, away_team_id: e.target.value })}>
                  <option value="">Away team</option>
                  {teams.map(t => <option key={t.id} value={t.id}>{t.name}</option>)}
                </select>
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
                <input data-testid="gdo-sched-new-date" className="input-field" type="date" value={newGame.game_date} onChange={e => setNewGame({ ...newGame, game_date: e.target.value })} />
                <input data-testid="gdo-sched-new-time" className="input-field" placeholder="Time (e.g. 3:00 PM)" value={newGame.game_time} onChange={e => setNewGame({ ...newGame, game_time: e.target.value })} />
              </div>
              <input data-testid="gdo-sched-new-location" className="input-field" placeholder="Location" value={newGame.location} onChange={e => setNewGame({ ...newGame, location: e.target.value })} />
              <input data-testid="gdo-sched-new-age" className="input-field" placeholder="Age group (optional, e.g. 11U)" value={newGame.age_group} onChange={e => setNewGame({ ...newGame, age_group: e.target.value })} />
              <div style={{ display: 'flex', gap: 8, marginTop: 4 }}>
                <button onClick={() => setShowNewGame(false)} style={{ flex: 1, padding: 10, background: 'transparent', border: '1px solid var(--dark-border)', borderRadius: 8, color: '#fff', fontWeight: 600, cursor: 'pointer' }}>Cancel</button>
                <button data-testid="gdo-sched-new-save" onClick={createAndAssign} disabled={busy} className="btn-primary" style={{ flex: 1, opacity: busy ? 0.6 : 1 }}>{busy ? 'Creating…' : 'Create & Assign'}</button>
              </div>
            </div>
          </div>
        )}

        {error && <p data-testid="gdo-schedule-error" style={{ color: '#f44336', fontSize: 13, marginBottom: 8 }}>{error}</p>}

        <div style={{ marginTop: 8 }}>
          <button onClick={onClose} style={{ width: '100%', padding: 10, background: 'transparent', border: '1px solid var(--dark-border)', borderRadius: 10, color: '#fff', fontWeight: 600, cursor: 'pointer' }}>Close</button>
        </div>
      </div>
    </div>
  );
};

/* ═══ Subscriptions Tab — ysv_admin / league_admin / pro_league_admin ═══
 *
 * Pro League Admin is auto-scoped to their own league (read from user.league_id,
 * no picker) and sees a league summary + per-team breakdown of active paid
 * subscriptions vs total members — useful for spotting teams with low
 * conversion at a glance.
 *
 * ysv_admin / league_admin get a league picker so they can audit subscription
 * health across any league (league_admin's picker mirrors the leagues array
 * already scoped to their own league by the backend's /leagues endpoint).
 */
const SubscriptionsTab = ({ api, role, user, leagues, leagueSubStats, setLeagueSubStats }) => {
  const isProLeague = role === "pro_league_admin";
  // Pro League Admin is locked to their own league; everyone else uses a picker.
  const [selectedLeagueId, setSelectedLeagueId] = useState(
    isProLeague ? (user?.league_id || "") : (leagues[0]?.id || "")
  );
  const [stats, setStats] = useState(isProLeague ? leagueSubStats : null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // Non-PLA roles auto-pick the first league once it loads.
  useEffect(() => {
    if (!isProLeague && !selectedLeagueId && leagues.length > 0) {
      setSelectedLeagueId(leagues[0].id);
    }
  }, [isProLeague, selectedLeagueId, leagues]);

  // Fetch stats whenever the selected league changes.
  useEffect(() => {
    if (!selectedLeagueId) { setStats(null); return; }
    let cancelled = false;
    setLoading(true);
    setError(null);
    api().get(`/leagues/${selectedLeagueId}/subscription-stats`)
      .then(r => {
        if (cancelled) return;
        setStats(r.data);
        // Cache the Pro League Admin's stats up to the parent so the Orgs-view tile stays in sync
        if (isProLeague) setLeagueSubStats(r.data);
      })
      .catch(e => {
        if (cancelled) return;
        setError(e.response?.data?.detail || "Failed to load subscription stats");
        setStats(null);
      })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [api, selectedLeagueId, isProLeague, setLeagueSubStats]);

  // Sort teams by lowest conversion first — these need the most attention
  const sortedTeams = (stats?.by_team || []).slice().sort((a, b) => {
    const pa = a.total > 0 ? a.active / a.total : 1;
    const pb = b.total > 0 ? b.active / b.total : 1;
    return pa - pb;
  });

  const totalPct = stats && stats.total_members > 0
    ? Math.round(100 * stats.active_subscriptions / stats.total_members)
    : 0;

  const leagueName = leagues.find(l => l.id === selectedLeagueId)?.name || "—";

  return (
    <div data-testid="subscriptions-tab" style={{ display: 'flex', flexDirection: 'column', gap: 18 }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 16, flexWrap: 'wrap' }}>
        <div>
          <h2 style={{ fontSize: 18, fontWeight: 600, display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4 }}>
            <DollarSign size={20} style={{ color: '#4caf50' }} /> League Subscriptions
          </h2>
          <p style={{ color: 'var(--text-secondary)', fontSize: 13 }}>
            {isProLeague
              ? "Active subscriptions for players, coaches, and amateurs across every team in your league."
              : "Audit league-wide subscription health. Pick a league below to drill in."}
          </p>
        </div>
        {!isProLeague && (
          <select
            data-testid="subs-league-picker"
            className="input-field"
            value={selectedLeagueId}
            onChange={e => setSelectedLeagueId(e.target.value)}
            style={{ minWidth: 220 }}
          >
            <option value="">— Select League —</option>
            {leagues.map(l => <option key={l.id} value={l.id}>{l.name}</option>)}
          </select>
        )}
      </div>

      {/* Top-line KPI tiles */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: 12 }}>
        <div className="glass-card" data-testid="subs-kpi-active" style={{ padding: 16 }}>
          <p style={{ fontSize: 10, color: 'var(--text-secondary)', textTransform: 'uppercase', letterSpacing: 0.5, fontWeight: 600 }}>Active Subscriptions</p>
          <p style={{ fontSize: 28, fontWeight: 700, color: '#4caf50', marginTop: 4 }}>
            {loading ? "…" : (stats?.active_subscriptions ?? 0)}
          </p>
        </div>
        <div className="glass-card" data-testid="subs-kpi-total" style={{ padding: 16 }}>
          <p style={{ fontSize: 10, color: 'var(--text-secondary)', textTransform: 'uppercase', letterSpacing: 0.5, fontWeight: 600 }}>Total Members</p>
          <p style={{ fontSize: 28, fontWeight: 700, marginTop: 4 }}>
            {loading ? "…" : (stats?.total_members ?? 0)}
          </p>
        </div>
        <div className="glass-card" data-testid="subs-kpi-pct" style={{ padding: 16 }}>
          <p style={{ fontSize: 10, color: 'var(--text-secondary)', textTransform: 'uppercase', letterSpacing: 0.5, fontWeight: 600 }}>Conversion</p>
          <p style={{ fontSize: 28, fontWeight: 700, color: 'var(--cyan)', marginTop: 4 }}>
            {loading ? "…" : `${totalPct}%`}
          </p>
        </div>
        <div className="glass-card" data-testid="subs-kpi-league" style={{ padding: 16 }}>
          <p style={{ fontSize: 10, color: 'var(--text-secondary)', textTransform: 'uppercase', letterSpacing: 0.5, fontWeight: 600 }}>League</p>
          <p style={{ fontSize: 16, fontWeight: 600, marginTop: 4 }}>{leagueName}</p>
        </div>
      </div>

      {error && (
        <div data-testid="subs-error" style={{ padding: 12, background: 'rgba(244,67,54,0.08)', border: '1px solid rgba(244,67,54,0.3)', borderRadius: 8, color: '#f44336', fontSize: 13 }}>{error}</div>
      )}

      {/* Per-team breakdown */}
      <div>
        <h3 style={{ fontSize: 14, fontWeight: 700, color: 'var(--text-secondary)', textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: 10 }}>
          Team Breakdown ({sortedTeams.length})
        </h3>

        {loading && <p style={{ color: 'var(--text-secondary)', fontSize: 13, textAlign: 'center', padding: 40 }}>Loading…</p>}

        {!loading && sortedTeams.length === 0 && !error && (
          <div data-testid="subs-empty" style={{ padding: 24, background: 'rgba(255,255,255,0.02)', borderRadius: 12, border: '1px dashed var(--dark-border)', textAlign: 'center' }}>
            <p style={{ fontSize: 14, color: 'var(--text-secondary)' }}>
              {selectedLeagueId ? "No teams or members yet in this league." : "Pick a league above to load subscription stats."}
            </p>
          </div>
        )}

        {!loading && sortedTeams.length > 0 && (
          <div data-testid="subs-team-list" style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            {sortedTeams.map(t => {
              const pct = t.total > 0 ? Math.round(100 * t.active / t.total) : 0;
              const tone = pct >= 70 ? '#4caf50' : pct >= 40 ? '#ff9800' : '#f44336';
              return (
                <div
                  key={t.team_id}
                  data-testid={`subs-team-row-${t.team_id}`}
                  className="glass-card"
                  style={{ padding: '14px 18px', display: 'flex', alignItems: 'center', gap: 14, flexWrap: 'wrap' }}
                >
                  <div style={{ flex: '1 1 220px', minWidth: 180 }}>
                    <p style={{ fontSize: 14, fontWeight: 600 }}>{t.team_name || "(unnamed team)"}</p>
                    <p style={{ fontSize: 12, color: 'var(--text-secondary)', marginTop: 2 }}>
                      <span style={{ color: '#4caf50', fontWeight: 600 }}>{t.active}</span>
                      <span> / {t.total} subscribed</span>
                    </p>
                  </div>
                  <div style={{ flex: '2 1 240px', minWidth: 160 }}>
                    <div style={{ position: 'relative', height: 10, background: 'rgba(255,255,255,0.06)', borderRadius: 6, overflow: 'hidden' }}>
                      <div data-testid={`subs-bar-${t.team_id}`} style={{
                        position: 'absolute', top: 0, left: 0, bottom: 0,
                        width: `${pct}%`, background: tone, transition: 'width 0.3s ease',
                      }} />
                    </div>
                  </div>
                  <div style={{ minWidth: 60, textAlign: 'right' }}>
                    <span data-testid={`subs-pct-${t.team_id}`} style={{ fontSize: 16, fontWeight: 700, color: tone }}>
                      {pct}%
                    </span>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
};
